import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, Heart, Brain, Star, Atom, Zap, Shield, Globe, Mic, Crown, Users, Eye, CreditCard } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import BookingCalendar from '@/components/BookingCalendar';
import VoiceRecorder from '@/components/VoiceRecorder';
import MembershipSection from '@/components/MembershipSection';
import { useLanguage, LanguageToggle } from '@/i18n/LanguageContext';

const ClientsSection = () => {
  const [clients, setClients] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    // Fetch client names
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backendUrl}/api/clients/names`)
      .then(res => res.json())
      .then(data => setClients(data))
      .catch(err => console.error('Failed to fetch clients:', err));
  }, []);

  return (
    <div className="mt-32">
      <div className="text-center mb-12">
        <div className="inline-block bg-yellow-500/20 px-6 py-2 rounded-full mb-6 border border-yellow-400/40">
          <span className="text-yellow-300 font-bold text-sm">OUR HEALING COMMUNITY</span>
        </div>
        <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
          Clients We've Served
        </h3>
        <p className="text-purple-200 text-lg">Join our growing community of transformed lives</p>
      </div>

      <div className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 backdrop-blur-xl border-2 border-purple-400/30 rounded-3xl p-6 md:p-12">
        {clients.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {clients.map((client, index) => (
              <div 
                key={index}
                className="bg-purple-800/30 backdrop-blur-sm border border-purple-400/20 rounded-xl p-4 text-center hover:bg-purple-700/40 transition-all hover:scale-105"
              >
                <p className="text-white font-medium">{client.name}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-purple-300">Loading client names...</p>
          </div>
        )}
      </div>
    </div>
  );
};

const QuantumParticleField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = [];
    const count = window.innerWidth < 768 ? 30 : 50;
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        size: Math.random() * 1.5 + 0.5,
        color: ['#a855f7', '#c084fc', '#06b6d4'][Math.floor(Math.random() * 3)],
        pulse: Math.random() * Math.PI * 2
      });
    }

    let frame = 0;
    const animate = () => {
      frame++;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.shadowBlur = 0;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.02;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.globalAlpha = 0.4 + Math.sin(p.pulse) * 0.2;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Draw lines only every 3rd frame to reduce GPU load
        if (frame % 3 === 0) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[j].x - p.x;
            const dy = particles[j].y - p.y;
            const distSq = dx * dx + dy * dy;
            if (distSq < 10000) { // 100px radius, avoid sqrt
              ctx.beginPath();
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = p.color;
              ctx.globalAlpha = (1 - distSq / 10000) * 0.12;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      }
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animId);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-[1]" style={{ mixBlendMode: 'screen' }} />;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [liveStats, setLiveStats] = useState({ active_visitors: 0, logged_in_users: 0, therapist_online: false });
  const visitorIdRef = useRef(localStorage.getItem('dq_visitor_id') || '');

  useEffect(() => {
    // Fetch live stats
    const fetchStats = () => {
      fetch(`${BACKEND_URL}/api/tracking/stats`).then(r => r.json()).then(setLiveStats).catch(() => {});
    };
    fetchStats();
    const statsInterval = setInterval(fetchStats, 15000);

    // Heartbeat: tell backend we're here
    const sendHeartbeat = () => {
      fetch(`${BACKEND_URL}/api/tracking/heartbeat?page=home${visitorIdRef.current ? '&visitor_id=' + visitorIdRef.current : ''}`, { method: 'POST' })
        .then(r => r.json())
        .then(d => {
          if (d.visitor_id && !visitorIdRef.current) {
            visitorIdRef.current = d.visitor_id;
            localStorage.setItem('dq_visitor_id', d.visitor_id);
          }
        })
        .catch(() => {});
    };
    sendHeartbeat();
    const hbInterval = setInterval(sendHeartbeat, 30000);

    return () => { clearInterval(statsInterval); clearInterval(hbInterval); };
  }, []);

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Deep space gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-950/90 via-indigo-950/80 to-black/90"></div>

      {/* Background quantum energy image */}
      <div className="absolute inset-0 opacity-15">
        <img 
          src="https://images.unsplash.com/photo-1621435410603-749c3094eb6c?w=1200&q=60"
          alt="Quantum Energy"
          className="w-full h-full object-cover"
          loading="lazy"
          style={{mixBlendMode: 'screen'}}
        />
      </div>

      {/* Quantum Particle Field - Interactive entanglement lines */}
      <QuantumParticleField />

      {/* MASSIVE glowing quantum orbs - Very prominent */}
      <div className="absolute inset-0 opacity-70">
        {/* Top left - Large pink-purple orb */}
        <div className="absolute top-0 left-0 w-96 h-96 -translate-x-1/3 -translate-y-1/3">
          <div className="absolute inset-0 bg-gradient-to-br from-pink-500 via-purple-500 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDuration: '3s'}}></div>
          <div className="absolute inset-8 bg-gradient-to-br from-pink-400 via-purple-400 to-transparent rounded-full blur-2xl animate-pulse" style={{animationDuration: '2s', animationDelay: '0.5s'}}></div>
        </div>

        {/* Top right - Large cyan orb */}
        <div className="absolute top-0 right-0 w-80 h-80 translate-x-1/4 -translate-y-1/4">
          <div className="absolute inset-0 bg-gradient-to-bl from-cyan-400 via-blue-500 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s', animationDelay: '0.5s'}}></div>
          <div className="absolute inset-8 bg-cyan-300 rounded-full blur-2xl opacity-80"></div>
        </div>

        {/* Center - Moving energy orb */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-full blur-3xl animate-pulse" style={{animationDuration: '3.5s', animationDelay: '1s'}}></div>
        </div>

        {/* Bottom left - Orange-pink orb */}
        <div className="absolute bottom-0 left-1/4 w-64 h-64 -translate-y-1/3">
          <div className="absolute inset-0 bg-gradient-to-tr from-orange-500 via-pink-500 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDuration: '4.5s', animationDelay: '1.5s'}}></div>
        </div>

        {/* Bottom right - Purple-indigo orb */}
        <div className="absolute bottom-0 right-1/4 w-72 h-72 translate-y-1/4">
          <div className="absolute inset-0 bg-gradient-to-tl from-indigo-600 via-purple-600 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDuration: '3.8s', animationDelay: '0.8s'}}></div>
        </div>
      </div>

      {/* Animated DNA helix-style lines */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="helixGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{stopColor: '#ec4899', stopOpacity: 0.8}} />
              <stop offset="50%" style={{stopColor: '#8b5cf6', stopOpacity: 1}} />
              <stop offset="100%" style={{stopColor: '#06b6d4', stopOpacity: 0.8}} />
            </linearGradient>
          </defs>
          {/* Curved flowing lines */}
          <path d="M 0 200 Q 400 100 800 200 T 1600 200" stroke="url(#helixGradient)" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M 0 400 Q 400 300 800 400 T 1600 400" stroke="url(#helixGradient)" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M 0 600 Q 400 500 800 600 T 1600 600" stroke="url(#helixGradient)" strokeWidth="2" fill="none" opacity="0.6" />
        </svg>
      </div>

      {/* Starfield effect with twinkling particles */}
      <div className="absolute inset-0 opacity-80">
        {[...Array(100)].map((_, i) => {
          const size = Math.random() * 3 + 1;
          const colors = ['bg-white', 'bg-cyan-300', 'bg-pink-300', 'bg-purple-300', 'bg-orange-300'];
          const randomColor = colors[Math.floor(Math.random() * colors.length)];
          return (
            <div
              key={i}
              className={`absolute ${randomColor} rounded-full animate-pulse`}
              style={{
                width: `${size}px`,
                height: `${size}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 2 + 2}s`,
                boxShadow: `0 0 ${size * 3}px currentColor`
              }}
            />
          );
        })}
      </div>

      {/* Floating energy waves - Aurora effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-0 w-full h-96 bg-gradient-to-r from-purple-500/20 via-pink-500/30 to-cyan-500/20 blur-3xl animate-pulse transform -skew-y-12" style={{animationDuration: '8s'}}></div>
          <div className="absolute bottom-40 right-0 w-full h-80 bg-gradient-to-l from-cyan-500/20 via-purple-500/30 to-orange-500/20 blur-3xl animate-pulse transform skew-y-12" style={{animationDuration: '9s', animationDelay: '2s'}}></div>
        </div>
      </div>

      {/* Quantum grid with glowing connections */}
      <div className="absolute inset-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px)
          `,
          backgroundSize: '100px 100px'
        }}></div>
      </div>

      {/* Hero Section */}
      <div className="relative z-10 container mx-auto px-6 py-20">
        {/* Navigation */}
        <nav className="flex flex-col md:flex-row gap-4 justify-between items-center mb-12 md:mb-20">
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.05em', textShadow: '0 0 20px rgba(168,85,247,0.6)'}}>Divine Quanta</h1>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
            <LanguageToggle />
            <a 
              href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20a%20Zoom%20Session%20to%20understand%20the%20science%20of%20quantum%20healing"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 sm:px-5 sm:py-3 rounded-full transition-colors shadow-lg text-xs sm:text-sm"
              data-testid="nav-zoom-btn"
              style={{boxShadow: '0 0 20px rgba(37, 99, 235, 0.4)'}}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span className="font-semibold">🎓 Zoom</span>
            </a>
            <a 
              href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20an%20Aura%20Analysis%20(₹2,000%20%7C%20Members%20₹1,600).%20Please%20send%20me%20the%20report%20on%20WhatsApp."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 sm:px-5 sm:py-3 rounded-full transition-colors shadow-lg text-xs sm:text-sm"
              data-testid="nav-aura-btn"
              style={{boxShadow: '0 0 20px rgba(147, 51, 234, 0.4)'}}
            >
              <span className="font-semibold">🔮 Aura</span>
            </a>
            <a 
              href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20a%20Chakra%20Analysis%20(₹2,000%20%7C%20Members%20₹1,600).%20Please%20send%20me%20the%20report%20on%20WhatsApp."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-violet-600 hover:bg-violet-700 text-white px-3 py-2 sm:px-5 sm:py-3 rounded-full transition-colors shadow-lg text-xs sm:text-sm"
              data-testid="nav-chakra-btn"
              style={{boxShadow: '0 0 20px rgba(124, 58, 237, 0.4)'}}
            >
              <span className="font-semibold">⚡ Chakra</span>
            </a>
            <a 
              href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20a%20quantum%20healing%20session%20with%20Divine%20Quanta"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 sm:px-5 sm:py-3 rounded-full transition-colors shadow-lg text-xs sm:text-sm"
              data-testid="nav-whatsapp-btn"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span className="font-semibold">{t('Book', 'बुक करें')}</span>
            </a>
            <Button 
              data-testid="nav-testimonials-btn"
              onClick={() => navigate('/testimonials')} 
              variant="ghost"
              className="text-purple-200 hover:text-white hover:bg-white/10 text-xs sm:text-sm"
            >
              {t('Testimonials', 'अनुभव')}
            </Button>
            <Button 
              data-testid="nav-login-btn"
              onClick={() => navigate('/auth')} 
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 sm:px-5 rounded-full shadow-lg text-xs sm:text-sm"
              style={{boxShadow: '0 0 20px rgba(168,85,247,0.5)'}}
            >
              {t('Login', 'लॉगिन')}
            </Button>
          </div>
        </nav>

        {/* Live Status Bar */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-8" data-testid="live-status-bar">
          {/* Therapist Online Status */}
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border text-sm ${
            liveStats.therapist_online
              ? 'bg-green-500/15 border-green-400/40 text-green-300'
              : 'bg-gray-500/15 border-gray-400/30 text-gray-400'
          }`}>
            <div className={`w-2.5 h-2.5 rounded-full ${liveStats.therapist_online ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`}></div>
            <span className="font-medium">{t('Therapist', 'थेरेपिस्ट')} {liveStats.therapist_online ? t('Online', 'ऑनलाइन') : t('Offline', 'ऑफलाइन')}</span>
          </div>

          {/* Active Viewers */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border border-cyan-400/30 bg-cyan-500/10 text-cyan-300 text-sm">
            <Eye className="w-4 h-4" />
            <span><strong>{liveStats.active_visitors}</strong> {t('viewing now', 'अभी देख रहे हैं')}</span>
          </div>

          {/* Logged In Users */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border border-purple-400/30 bg-purple-500/10 text-purple-300 text-sm">
            <Users className="w-4 h-4" />
            <span><strong>{liveStats.logged_in_users}</strong> {t('logged in', 'लॉग इन हैं')}</span>
          </div>
        </div>

        {/* Therapist Message Sidebar - Compact */}
        {/* Hero Content */}
        <div className="grid md:grid-cols-2 gap-12 items-center mt-16">
          <div className="space-y-8">
            <div className="inline-block max-w-full">
              <span className="inline-block bg-purple-500/40 text-cyan-200 px-5 py-3 sm:px-6 rounded-2xl sm:rounded-full text-sm sm:text-base backdrop-blur-md border border-cyan-400/40 shadow-lg leading-relaxed" style={{boxShadow: '0 0 20px rgba(103,232,249,0.3)'}}>
                <Atom className="w-5 h-5 inline mr-2" />
                {t('Quantum Entanglement Therapy - Heal from Anywhere in the World', 'क्वांटम एंटैंगलमेंट थेरेपी — दुनिया में कहीं से भी उपचार पाएं')}
              </span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('The Science of', 'विज्ञान —')}<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300">{t('Quantum Healing', 'क्वांटम हीलिंग का')}</span>
            </h2>
            <p className="text-xl lg:text-2xl text-purple-200 max-w-4xl mx-auto leading-relaxed mb-8">
              {t(<>Harnessing the power of <span className="text-cyan-300 font-bold">quantum entanglement</span> and <span className="text-pink-300 font-bold">wave-particle duality</span> for revolutionary wellness transformation. Based on German quantum physics technology - working at the <span className="text-purple-300 font-bold">subatomic level</span> to heal DNA, cells, and energy fields.</>,
                <><span className="text-cyan-300 font-bold">क्वांटम एंटैंगलमेंट</span> और <span className="text-pink-300 font-bold">तरंग-कण द्वैत</span> की शक्ति से क्रांतिकारी वेलनेस परिवर्तन। जर्मन क्वांटम भौतिकी तकनीक पर आधारित — <span className="text-purple-300 font-bold">परमाणु से भी सूक्ष्म स्तर</span> पर DNA, कोशिकाओं और ऊर्जा क्षेत्रों का उपचार।</>)}
            </p>

            {/* Quantum Physics Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-cyan-500/10 border border-cyan-400/30 rounded-xl p-3 text-center backdrop-blur-sm">
                <p className="text-cyan-300 text-2xl font-bold">10<sup className="text-sm">-35</sup>m</p>
                <p className="text-cyan-200 text-xs mt-1">{t('Planck Scale Healing', 'प्लांक स्केल हीलिंग')}</p>
              </div>
              <div className="bg-purple-500/10 border border-purple-400/30 rounded-xl p-3 text-center backdrop-blur-sm">
                <p className="text-purple-300 text-2xl font-bold">7.83Hz</p>
                <p className="text-purple-200 text-xs mt-1">{t('Schumann Resonance', 'शूमान रेज़ोनेंस')}</p>
              </div>
              <div className="bg-pink-500/10 border border-pink-400/30 rounded-xl p-3 text-center backdrop-blur-sm">
                <p className="text-pink-300 text-2xl font-bold">∞</p>
                <p className="text-pink-200 text-xs mt-1">{t('Distance No Barrier', 'दूरी कोई बाधा नहीं')}</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-500/15 to-purple-500/15 backdrop-blur-md border-2 border-cyan-400/30 rounded-2xl px-8 py-6 max-w-3xl mx-auto">
              <p className="text-cyan-200 text-lg font-semibold flex items-center gap-2">
                <Globe className="w-5 h-5" /> {t('Quantum Studios in Delhi & Goa | Healing Worldwide', 'दिल्ली और गोवा में क्वांटम स्टूडियो | पूरी दुनिया में उपचार')}
              </p>
              <p className="text-purple-100 text-base mt-2">
                {t(<>Using <span className="text-cyan-300 font-bold">quantum entanglement</span> principles - your particles are connected to the healing field regardless of physical distance</>,
                  <><span className="text-cyan-300 font-bold">क्वांटम एंटैंगलमेंट</span> के सिद्धांतों से — आपके कण भौतिक दूरी की परवाह किए बिना हीलिंग फील्ड से जुड़े रहते हैं</>)}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                data-testid="hero-book-session-btn"
                onClick={() => navigate('/pay')} 
                className="bg-gradient-to-r from-cyan-600 via-purple-600 to-indigo-600 hover:from-cyan-700 hover:via-purple-700 hover:to-indigo-700 text-white px-8 py-6 rounded-full text-lg font-semibold"
                style={{boxShadow: '0 0 30px rgba(103,232,249,0.3), 0 0 60px rgba(168,85,247,0.2)'}}
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {t('Book & Pay Now', 'बुक करें और भुगतान करें')}
              </Button>
              <Button 
                data-testid="hero-learn-more-btn"
                onClick={() => document.getElementById('intro-video').scrollIntoView({ behavior: 'smooth' })}
                variant="outline" 
                className="border-2 border-cyan-400/40 text-cyan-200 hover:bg-cyan-500/10 px-8 py-6 rounded-full text-lg backdrop-blur-sm"
              >
                <Atom className="w-5 h-5 mr-2" />
                {t('Explore the Science', 'विज्ञान को जानें')}
              </Button>
            </div>
          </div>

          {/* Quantum Atom Visual */}
          <div className="hidden md:flex justify-center items-center">
            <div className="relative w-80 h-80">
              {/* Orbital rings */}
              <div className="absolute inset-0 rounded-full border border-cyan-400/30 animate-spin" style={{animationDuration: '20s'}}></div>
              <div className="absolute inset-4 rounded-full border border-purple-400/40 animate-spin" style={{animationDuration: '15s', animationDirection: 'reverse'}}></div>
              <div className="absolute inset-8 rounded-full border border-pink-400/30 animate-spin" style={{animationDuration: '12s'}}></div>

              {/* Electrons */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-cyan-400 rounded-full animate-spin shadow-lg" style={{animationDuration: '20s', boxShadow: '0 0 15px rgba(34,211,238,0.8)'}}></div>
              <div className="absolute bottom-4 right-4 w-3 h-3 bg-purple-400 rounded-full animate-spin shadow-lg" style={{animationDuration: '15s', animationDirection: 'reverse', boxShadow: '0 0 15px rgba(192,132,252,0.8)'}}></div>
              <div className="absolute top-8 left-8 w-3 h-3 bg-pink-400 rounded-full animate-spin shadow-lg" style={{animationDuration: '12s', boxShadow: '0 0 15px rgba(244,114,182,0.8)'}}></div>

              {/* Nucleus */}
              <div className="absolute inset-24 rounded-full bg-gradient-to-br from-purple-600/60 via-cyan-500/40 to-pink-500/60 blur-2xl animate-pulse" style={{animationDuration: '3s'}}></div>
              <div className="absolute inset-28 rounded-full bg-gradient-to-br from-white/20 to-purple-400/30 backdrop-blur-xl border border-white/20 flex items-center justify-center">
                <span className="text-white text-4xl font-bold" style={{fontFamily: 'Cormorant Garamond, serif', textShadow: '0 0 20px rgba(168,85,247,0.8)'}}>Q</span>
              </div>

              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-radial from-purple-500/10 via-transparent to-transparent rounded-full"></div>
            </div>
          </div>
        </div>

        {/* ============ WHAT WE HEAL - Areas We Deal In ============ */}
        <div className="mt-32" data-testid="areas-we-deal-section">
          <div className="text-center mb-16">
            <div className="inline-block bg-cyan-500/20 px-6 py-2 rounded-full mb-6 border border-cyan-400/40">
              <span className="text-cyan-300 font-bold text-sm">{t('DISCOVER WHAT WE HEAL', 'जानिए हम क्या ठीक करते हैं')}</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t(<>Areas We <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300">Specialize In</span></>,
                <>हमारी <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300">विशेषज्ञता के क्षेत्र</span></>)}
            </h3>
            <p className="text-purple-200 text-lg max-w-3xl mx-auto">
              {t('Using quantum physics and energy science, we address deep-rooted issues across physical, emotional, mental, and spiritual dimensions',
                'क्वांटम भौतिकी और ऊर्जा विज्ञान के माध्यम से हम शारीरिक, भावनात्मक, मानसिक और आध्यात्मिक स्तर की गहरी समस्याओं का समाधान करते हैं')}
            </p>
          </div>

          {/* Healing Areas Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
            {[
              { title: 'Anxiety & Stress', desc: 'Deep quantum frequency modulation to calm the nervous system and restore inner peace', icon: Brain, color: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-400/30', text: 'text-blue-300' },
              { title: 'Chronic Health Issues', desc: 'Long-standing physical conditions addressed at the cellular and DNA level through quantum resonance', icon: Heart, color: 'from-red-500/20 to-pink-500/20', border: 'border-red-400/30', text: 'text-red-300' },
              { title: 'Mental Disorders', desc: 'Depression, OCD, PTSD and other mental health challenges treated through quantum field therapy', icon: Brain, color: 'from-purple-500/20 to-violet-500/20', border: 'border-purple-400/30', text: 'text-purple-300' },
              { title: 'Childhood Patterns', desc: 'Unresolved childhood traumas and behavioral patterns healed at the subconscious quantum level', icon: Shield, color: 'from-emerald-500/20 to-green-500/20', border: 'border-emerald-400/30', text: 'text-emerald-300' },
              { title: 'Ancestral Healing', desc: 'Clearing generational karmic patterns and inherited energy blockages from your DNA blueprint', icon: Sparkles, color: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-400/30', text: 'text-amber-300' },
              { title: 'Post-Disease Recovery', desc: 'Improving cell life and regeneration after serious diseases including cancer recovery support', icon: Zap, color: 'from-teal-500/20 to-cyan-500/20', border: 'border-teal-400/30', text: 'text-teal-300' },
              { title: 'Relationship Healing', desc: 'Resolving emotional conflicts, codependency, heartbreak and restoring harmonious connections', icon: Heart, color: 'from-pink-500/20 to-rose-500/20', border: 'border-pink-400/30', text: 'text-pink-300' },
              { title: 'Spiritual Awakening', desc: 'Kundalini activation, chakra alignment, aura cleansing and connecting with your higher self', icon: Star, color: 'from-indigo-500/20 to-purple-500/20', border: 'border-indigo-400/30', text: 'text-indigo-300' },
            ].map((area, i) => {
              const Icon = area.icon;
              return (
                <div key={i} className={`bg-gradient-to-br ${area.color} backdrop-blur-sm border ${area.border} rounded-2xl p-6 hover:scale-[1.03] transition-all`} style={{transition: 'all 0.3s ease'}}>
                  <div className={`${area.text} bg-black/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4 border ${area.border}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h4 className={`text-lg font-bold ${area.text} mb-2`}>{area.title}</h4>
                  <p className="text-purple-200/80 text-sm leading-relaxed">{area.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Our Verticals — Personal | Corporate | Schools | Technology */}
          <div className="text-center mb-10">
            <div className="inline-block bg-cyan-500/20 px-6 py-2 rounded-full mb-4 border border-cyan-400/40">
              <span className="text-cyan-300 font-bold text-sm">{t('OUR VERTICALS', 'हमारी सेवाएँ')}</span>
            </div>
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-3" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t(<>How Quantum Healing <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300">Reaches You</span></>,
                <>क्वांटम हीलिंग <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300">आप तक कैसे पहुँचती है</span></>)}
            </h3>
            <p className="text-purple-200 text-base max-w-3xl mx-auto">
              {t('Four dedicated verticals — one science of quantum physics touching every part of life',
                'चार समर्पित क्षेत्र — क्वांटम भौतिकी का एक ही विज्ञान, जीवन के हर पहलू को छूता हुआ')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Personal Healing */}
            <div className="relative bg-gradient-to-br from-purple-900/60 to-indigo-900/60 backdrop-blur-xl border-2 border-purple-400/40 rounded-3xl p-6 hover:border-purple-400/70 transition-all group flex flex-col" style={{transition: 'all 0.3s ease'}}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-t-3xl"></div>
              <div className="bg-purple-500/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4 border border-purple-400/30 group-hover:scale-110 transition-transform">
                <Heart className="w-7 h-7 text-purple-300" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>Personal Healing</h4>
              <p className="text-purple-200 text-sm mb-4 leading-relaxed flex-1">
                One-on-one quantum therapy sessions tailored to your unique energy blueprint. Distance healing available worldwide.
              </p>
              <ul className="space-y-1.5 mb-5">
                {['Prana Analysis & Aura Report', 'Chakra Balancing Sessions', 'DNA & Cellular Repair', 'Distance Healing (Worldwide)'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-purple-200 text-xs">
                    <div className="w-1 h-1 bg-purple-400 rounded-full flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/918920443069?text=Hi,%20I%20am%20interested%20in%20Personal%20Quantum%20Healing%20sessions.%20Please%20share%20details."
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all"
                data-testid="personal-healing-cta"
              >
                {t('Book Personal Session', 'व्यक्तिगत सत्र बुक करें')}
              </a>
            </div>

            {/* Corporate Wellness */}
            <div className="relative bg-gradient-to-br from-blue-900/60 to-cyan-900/60 backdrop-blur-xl border-2 border-blue-400/40 rounded-3xl p-6 hover:border-blue-400/70 transition-all group flex flex-col" style={{transition: 'all 0.3s ease'}}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-t-3xl"></div>
              <div className="bg-blue-500/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4 border border-blue-400/30 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('Corporate Wellness', 'कॉर्पोरेट वेलनेस')}</h4>
              <p className="text-blue-200 text-sm mb-4 leading-relaxed flex-1">
                {t(<>Bring quantum science into your organization. Boost productivity, department growth &amp; staff mental health.</>,
                  'अपने संगठन में क्वांटम विज्ञान लाएँ। उत्पादकता, विभागीय विकास और कर्मचारियों के मानसिक स्वास्थ्य को बढ़ावा दें।')}
              </p>
              <ul className="space-y-1.5 mb-5">
                {[t('Job Productivity & Focus', 'कार्य उत्पादकता और फोकस'), t('Department-wide Growth', 'विभागीय स्तर पर विकास'), t('Staff Mental Health', 'कर्मचारियों का मानसिक स्वास्थ्य'), t('Zoom / Meet Installations', 'ज़ूम / मीट इंस्टॉलेशन')].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-blue-200 text-xs">
                    <div className="w-1 h-1 bg-blue-400 rounded-full flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="#corporate-section"
                onClick={(e) => { e.preventDefault(); document.getElementById('corporate-section')?.scrollIntoView({behavior: 'smooth'}); }}
                className="mt-auto inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all"
                data-testid="corporate-vertical-cta"
              >
                {t('Explore Corporate', 'कॉर्पोरेट जानें')}
              </a>
            </div>

            {/* Schools */}
            <div className="relative bg-gradient-to-br from-emerald-900/60 to-teal-900/60 backdrop-blur-xl border-2 border-emerald-400/40 rounded-3xl p-6 hover:border-emerald-400/70 transition-all group flex flex-col" style={{transition: 'all 0.3s ease'}}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-t-3xl"></div>
              <div className="bg-emerald-500/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4 border border-emerald-400/30 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('Schools', 'स्कूल')}</h4>
              <p className="text-emerald-200 text-sm mb-4 leading-relaxed flex-1">
                {t(<>Install quantum science in counseling rooms. Support students, counselors &amp; teachers with therapy-backed wellness.</>,
                  'काउंसलिंग रूम में क्वांटम विज्ञान स्थापित करें। विद्यार्थियों, काउंसलरों और शिक्षकों को थेरेपी-आधारित वेलनेस से सहयोग दें।')}
              </p>
              <ul className="space-y-1.5 mb-5">
                {[t('Live Zoom for Counselors', 'काउंसलरों के लिए लाइव ज़ूम'), t('Therapy for Kids (6–18)', 'बच्चों के लिए थेरेपी (6–18)'), t('Focus, Anxiety & Sleep', 'फोकस, चिंता और नींद'), t('Emotional Recovery', 'भावनात्मक रिकवरी')].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-emerald-200 text-xs">
                    <div className="w-1 h-1 bg-emerald-400 rounded-full flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="#schools-section"
                onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="schools-section"]')?.scrollIntoView({behavior: 'smooth'}); }}
                className="mt-auto inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all"
                data-testid="schools-vertical-cta"
              >
                {t('Explore Schools', 'स्कूल जानें')}
              </a>
            </div>

            {/* Buy the Technology */}
            <div className="relative bg-gradient-to-br from-amber-900/60 to-yellow-900/60 backdrop-blur-xl border-2 border-amber-400/40 rounded-3xl p-6 hover:border-amber-400/70 transition-all group flex flex-col" style={{transition: 'all 0.3s ease'}}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-t-3xl"></div>
              <div className="bg-amber-500/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-4 border border-amber-400/30 group-hover:scale-110 transition-transform">
                <Atom className="w-7 h-7 text-amber-300" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('Buy the Technology', 'तकनीक खरीदें')}</h4>
              <p className="text-amber-200 text-sm mb-4 leading-relaxed flex-1">
                {t(<>Own quantum science therapy tech for personal or professional use. Full training &amp; certification included.</>,
                  'व्यक्तिगत या पेशेवर उपयोग के लिए क्वांटम थेरेपी तकनीक अपनाएँ। पूर्ण प्रशिक्षण और प्रमाणन शामिल।')}
              </p>
              <ul className="space-y-1.5 mb-5">
                {[t('Quantum Resonance Devices', 'क्वांटम रेज़ोनेंस उपकरण'), t('Full Training & Certification', 'पूर्ण प्रशिक्षण और प्रमाणन'), t('Zoom Support Sessions', 'ज़ूम सहायता सत्र'), t('Start Your Own Practice', 'अपनी प्रैक्टिस शुरू करें')].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-amber-200 text-xs">
                    <div className="w-1 h-1 bg-amber-400 rounded-full flex-shrink-0"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/918920443069?text=Hi,%20I%20am%20interested%20in%20buying%20Quantum%20Science%20Therapy%20technology.%20Please%20share%20details%20and%20pricing."
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black px-5 py-2.5 rounded-full text-sm font-bold transition-all"
                data-testid="buy-technology-cta"
              >
                {t('Inquire About Technology', 'तकनीक की जानकारी लें')}
              </a>
            </div>
          </div>
        </div>

        {/* Welcome Message from the Team */}
        <div className="mt-32" data-testid="welcome-message-section">
          <div className="relative max-w-5xl mx-auto">
            {/* Golden aura backdrop */}
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/15 via-amber-500/10 to-orange-500/15 blur-3xl rounded-full"></div>

            <div className="relative bg-gradient-to-br from-purple-900/60 via-indigo-950/70 to-purple-900/60 backdrop-blur-xl border-2 border-yellow-400/40 rounded-3xl p-8 md:p-12 shadow-2xl spiritual-glow">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Large opening quote mark */}
                <div className="flex-shrink-0">
                  <div className="text-8xl md:text-9xl leading-none text-yellow-300/70 font-serif" style={{fontFamily: 'Cormorant Garamond, serif'}}>&ldquo;</div>
                  <div className="mt-2 inline-block bg-yellow-500/20 px-4 py-1 rounded-full border border-yellow-400/40">
                    <span className="text-yellow-300 font-bold text-xs tracking-widest">{t('A PERSONAL WELCOME', 'हार्दिक स्वागत')}</span>
                  </div>
                </div>

                <div className="flex-1">
                  <p className="text-white text-lg md:text-xl leading-relaxed mb-6" style={{fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic'}}>
                    {t(<>Hi, this is the <strong className="not-italic text-yellow-300">Divine Quanta team</strong>. Welcome everybody to our powerful quantum healing studio, where we&apos;re working on <strong className="not-italic text-pink-300">quantum entanglement</strong> to heal your issues through distance &mdash; and it is as effective as a normal healing process.</>,
                      <>नमस्ते, हम हैं <strong className="not-italic text-yellow-300">डिवाइन क्वांटा टीम</strong>। हमारे शक्तिशाली क्वांटम हीलिंग स्टूडियो में आप सभी का स्वागत है, जहाँ हम <strong className="not-italic text-pink-300">क्वांटम एंटैंगलमेंट</strong> के माध्यम से दूरी से ही आपकी समस्याओं का उपचार करते हैं — और यह सामान्य उपचार जितना ही प्रभावी है।</>)}
                  </p>
                  <p className="text-white text-lg md:text-xl leading-relaxed mb-6" style={{fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic'}}>
                    {t(<>We&apos;re happy to launch this for the masses. This is the <strong className="not-italic text-yellow-300">very first online quantum healing studio in India</strong>, and we are happy to launch soon our <strong className="not-italic text-pink-300">offline presence in Delhi and Goa</strong> for everybody who is seeking personal sessions in different areas of their life.</>,
                      <>हमें इसे जन-जन तक पहुँचाने की खुशी है। यह <strong className="not-italic text-yellow-300">भारत का पहला ऑनलाइन क्वांटम हीलिंग स्टूडियो</strong> है, और जल्द ही हम <strong className="not-italic text-pink-300">दिल्ली और गोवा में ऑफलाइन उपस्थिति</strong> भी शुरू करने जा रहे हैं — उन सभी के लिए जो जीवन के विभिन्न क्षेत्रों में व्यक्तिगत सत्र चाहते हैं।</>)}
                  </p>

                  {/* Signature */}
                  <div className="flex items-center gap-4 pt-4 border-t border-yellow-400/25">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-400/40 to-amber-500/40 border-2 border-yellow-400/60 flex items-center justify-center">
                      <span className="text-yellow-200 font-bold text-lg" style={{fontFamily: 'Cormorant Garamond, serif'}}>DQ</span>
                    </div>
                    <div>
                      <p className="text-yellow-300 font-bold text-lg" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('Divine Quanta Team', 'डिवाइन क्वांटा टीम')}</p>
                      <p className="text-purple-200 text-sm">{t(<>Quantum Wellness Mentors &middot; Healers &amp; Therapists</>, 'क्वांटम वेलनेस मेंटर्स · हीलर्स और थेरेपिस्ट')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Meet Your Therapist Section */}
        <div className="mt-32">
          <div className="bg-gradient-to-br from-purple-800/40 to-indigo-900/40 backdrop-blur-xl border-2 border-yellow-400/40 rounded-3xl p-6 md:p-12 spiritual-glow">
            <div className="text-center mb-12">
              <div className="inline-block bg-yellow-500/20 px-6 py-2 rounded-full mb-6 border border-yellow-400/40">
                <span className="text-yellow-300 font-bold text-sm">{t('ABOUT YOUR THERAPIST', 'आपकी थेरेपिस्ट के बारे में')}</span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-100 to-yellow-300 mb-4" 
                  style={{
                    fontFamily: 'Cormorant Garamond, serif',
                    letterSpacing: '0.08em',
                    textShadow: '0 4px 8px rgba(0,0,0,0.8), 0 0 40px rgba(234,179,8,0.5), 0 0 60px rgba(234,179,8,0.3)',
                    filter: 'drop-shadow(0 4px 6px rgba(234,179,8,0.7))'
                  }}>
                {t('Meet Our Founder', 'हमारी संस्थापिका से मिलिए')}
              </h3>
              <div className="space-y-2 mb-6">
                <p className="text-yellow-300 text-lg font-semibold">
                  {t('Quantum Wellness Mentors | Healers | Therapists', 'क्वांटम वेलनेस मेंटर | हीलर | थेरेपिस्ट')}
                </p>
                <p className="text-pink-300 text-base font-medium">
                  Shamballa Buddhist Healers | Access Bars Practitioners | Relationship Counsellors | Life Coaches
                </p>
                <p className="text-purple-200 text-sm">
                  {t('15+ Years in Spiritual Healing & Holistic Wellness', 'आध्यात्मिक उपचार और समग्र वेलनेस में 15+ वर्षों का अनुभव')}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Profile Picture */}
              <div className="flex flex-col items-center">
                <div className="relative" data-testid="founder-photo-frame">
                  {/* Outer glow */}
                  <div className="absolute -inset-8 bg-gradient-to-r from-yellow-400 via-purple-400 to-indigo-400 rounded-full blur-3xl opacity-50 animate-pulse"></div>
                  {/* Rotating golden aura ring */}
                  <div
                    className="absolute -inset-3 rounded-full opacity-90"
                    style={{
                      background: 'conic-gradient(from 0deg, rgba(234,179,8,0.9), rgba(236,72,153,0.55), rgba(129,140,248,0.55), rgba(234,179,8,0.9))',
                      animation: 'founder-ring-spin 10s linear infinite',
                      filter: 'blur(1.5px)'
                    }}
                  ></div>
                  {/* Inner spacer ring */}
                  <div className="absolute -inset-1.5 rounded-full bg-purple-950"></div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_quantum-therapy-1/artifacts/xxp3kjrw_IMG_20260323_120537_592.jpg"
                    alt="Maa Anubhuti - Founder, Divine Quanta"
                    className="relative w-72 h-72 sm:w-[21rem] sm:h-[21rem] lg:w-[26rem] lg:h-[26rem] rounded-full object-cover border-4 border-yellow-300/70 shadow-2xl"
                    style={{objectPosition: 'center top', boxShadow: '0 0 60px rgba(234,179,8,0.35), 0 25px 60px rgba(0,0,0,0.5)'}}
                    loading="lazy"
                  />
                  {/* Soft vignette to blend background */}
                  <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{background: 'radial-gradient(circle at center, transparent 55%, rgba(59,7,100,0.45) 100%)'}}
                  ></div>
                  {/* Sparkle accents */}
                  <Sparkles className="absolute -top-2 right-8 w-8 h-8 text-yellow-300 opacity-90 animate-pulse" />
                  <Sparkles className="absolute bottom-4 -left-4 w-6 h-6 text-pink-300 opacity-80 animate-pulse" style={{animationDelay: '0.8s'}} />
                </div>
                <div className="mt-8 text-center" data-testid="founder-name">
                  <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-100 to-yellow-300"
                     style={{fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.06em', textShadow: '0 0 30px rgba(234,179,8,0.4)'}}>
                    Maa Anubhuti
                  </p>
                  <p className="text-pink-300 text-lg font-semibold tracking-widest mt-2">
                    ( OSHO SANYASIN )
                  </p>
                  <p className="text-purple-100 text-xl font-semibold mt-3" style={{fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.05em'}} data-testid="founder-legal-name">
                    Sonia R Gupta
                  </p>
                  <p className="text-purple-300 text-sm tracking-wide">{t('Founder, Divine Quanta', 'संस्थापिका, डिवाइन क्वांटा')}</p>
                  <div className="mx-auto mt-4 h-px w-40 bg-gradient-to-r from-transparent via-yellow-400/70 to-transparent"></div>
                </div>
              </div>

              {/* Bio & Message */}
              <div className="space-y-6">
                <div className="bg-purple-900/40 backdrop-blur-lg border-l-4 border-yellow-400 rounded-xl p-8">
                  <h4 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                    {t('About Our Founder', 'हमारी संस्थापिका के बारे में')}
                  </h4>
                  <div className="space-y-4 text-purple-100 leading-relaxed">
                    <p>
                      {t(<><strong className="text-yellow-300">Maa Anubhuti</strong> — born and brought up in <strong className="text-yellow-300">Delhi</strong> — brings over <strong className="text-yellow-300">15 years</strong> of deep spiritual and healing experience rooted in <strong className="text-pink-300">Buddhism</strong>, <strong className="text-pink-300">OSHO practices</strong>, and mindfulness-based living.</>,
                        <><strong className="text-yellow-300">माँ अनुभूति</strong> — <strong className="text-yellow-300">दिल्ली</strong> में जन्मी और पली-बढ़ीं — <strong className="text-pink-300">बौद्ध धर्म</strong>, <strong className="text-pink-300">ओशो साधनाओं</strong> और माइंडफुलनेस-आधारित जीवन में रचे-बसे <strong className="text-yellow-300">15 वर्षों</strong> से अधिक के गहन आध्यात्मिक और उपचार अनुभव के साथ।</>)}
                    </p>
                    <p>
                      {t(<>Her journey has been dedicated to understanding the human mind, energy, and consciousness at the deepest levels. She is a trained <strong className="text-yellow-300">Shamballa Buddhist Healer, Access Bars Practitioner, Relationship Counsellor, Life Coach, and Quantum Wellness Mentor &amp; Therapist</strong>.</>,
                        <>उनकी यात्रा मानव मन, ऊर्जा और चेतना को गहनतम स्तर पर समझने के लिए समर्पित रही है। वे प्रशिक्षित <strong className="text-yellow-300">शम्बाला बुद्धिस्ट हीलर, एक्सेस बार्स प्रैक्टिशनर, रिलेशनशिप काउंसलर, लाइफ कोच और क्वांटम वेलनेस मेंटर व थेरेपिस्ट</strong> हैं।</>)}
                    </p>
                    <p>
                      {t(<>Her work reaches beyond the surface — working on <strong className="text-pink-300">deeper DNA issues, karmic patterns, and emotional blockages</strong>, gently dissolving what no longer serves you and awakening what does.</>,
                        <>उनका कार्य सतह से कहीं गहरा है — <strong className="text-pink-300">गहरे DNA मुद्दों, कार्मिक पैटर्न और भावनात्मक अवरोधों</strong> पर काम करते हुए, जो अब आपके काम का नहीं उसे धीरे-से विसर्जित करना और जो है उसे जागृत करना।</>)}
                    </p>
                    <p>
                      {t(<>Having worked extensively in counseling and guiding individuals through emotional, mental, and life challenges, her work focuses on <strong className="text-yellow-300">holistic wellness</strong>&mdash;addressing not just symptoms, but the <strong className="text-pink-300">root causes within the subconscious and energy systems</strong>.</>,
                        <>भावनात्मक, मानसिक और जीवन की चुनौतियों में लोगों का मार्गदर्शन करते हुए उनका कार्य <strong className="text-yellow-300">समग्र वेलनेस</strong> पर केंद्रित है — केवल लक्षणों पर नहीं, बल्कि <strong className="text-pink-300">अवचेतन और ऊर्जा तंत्र में छिपे मूल कारणों</strong> पर।</>)}
                    </p>
                  </div>
                </div>

                {/* Our Team */}
                <div className="bg-purple-900/40 backdrop-blur-lg border-l-4 border-pink-400 rounded-xl p-8" data-testid="our-team-card">
                  <h4 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                    {t('Our Team of Experts', 'हमारी विशेषज्ञ टीम')}
                  </h4>
                  <p className="text-purple-100 leading-relaxed">
                    {t(<>Alongside our founder, our team members are <strong className="text-pink-300">experts from very diversified fields</strong>, helping for the cause. They will be helping you with <strong className="text-yellow-300">different segments as subject experts</strong> — helping you understand this science &amp; technology better, and also answering your <strong className="text-yellow-300">personal concerns in different areas</strong>.</>,
                      <>हमारी संस्थापिका के साथ, हमारी टीम के सदस्य <strong className="text-pink-300">विभिन्न क्षेत्रों के विशेषज्ञ</strong> हैं, जो इस उद्देश्य में सहयोग कर रहे हैं। वे <strong className="text-yellow-300">विषय-विशेषज्ञ के रूप में विभिन्न क्षेत्रों</strong> में आपकी सहायता करेंगे — इस विज्ञान और तकनीक को बेहतर समझने में, और <strong className="text-yellow-300">आपके व्यक्तिगत प्रश्नों</strong> के उत्तर देने में।</>)}
                  </p>
                </div>

                {/* Expertise Tags */}
                <div className="flex flex-wrap gap-2">
                  <span className="bg-purple-600/40 text-purple-200 px-3 py-1 rounded-full text-sm border border-purple-400/30">Buddhism</span>
                  <span className="bg-pink-600/40 text-pink-200 px-3 py-1 rounded-full text-sm border border-pink-400/30">OSHO Practices</span>
                  <span className="bg-yellow-600/40 text-yellow-200 px-3 py-1 rounded-full text-sm border border-yellow-400/30">Shamballa Buddhist Healing</span>
                  <span className="bg-amber-600/40 text-amber-200 px-3 py-1 rounded-full text-sm border border-amber-400/30">Access Bars</span>
                  <span className="bg-rose-600/40 text-rose-200 px-3 py-1 rounded-full text-sm border border-rose-400/30">Relationship Counselling</span>
                  <span className="bg-green-600/40 text-green-200 px-3 py-1 rounded-full text-sm border border-green-400/30">Life Coaching</span>
                  <span className="bg-cyan-600/40 text-cyan-200 px-3 py-1 rounded-full text-sm border border-cyan-400/30">DNA &amp; Karmic Healing</span>
                  <span className="bg-indigo-600/40 text-indigo-200 px-3 py-1 rounded-full text-sm border border-indigo-400/30">Quantum Wellness Mentor</span>
                </div>

                {/* Credentials */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-purple-900/30 backdrop-blur-lg border border-yellow-400/30 rounded-xl p-4 text-center">
                    <p className="text-yellow-300 text-3xl font-bold mb-2">15+</p>
                    <p className="text-purple-200 text-xs">{t('Years Experience', 'वर्षों का अनुभव')}</p>
                  </div>
                  <div className="bg-purple-900/30 backdrop-blur-lg border border-yellow-400/30 rounded-xl p-4 text-center">
                    <p className="text-yellow-300 text-3xl font-bold mb-2">5000+</p>
                    <p className="text-purple-200 text-xs">{t('Lives Transformed', 'जीवन परिवर्तित')}</p>
                  </div>
                  <div className="bg-purple-900/30 backdrop-blur-lg border border-yellow-400/30 rounded-xl p-4 text-center">
                    <p className="text-yellow-300 text-3xl font-bold mb-2">6+</p>
                    <p className="text-purple-200 text-xs">{t('Healing Modalities', 'उपचार पद्धतियाँ')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Clients We've Served Section */}
        <ClientsSection />

        {/* Booking Calendar Section */}
        <div className="mt-32">
          <BookingCalendar />
        </div>

        {/* YouTube Video Section */}
        <div id="intro-video" className="mt-32">
          <div className="text-center mb-12">
            <div className="inline-block bg-pink-500/20 px-6 py-2 rounded-full mb-6 border border-pink-400/40">
              <span className="text-pink-300 font-bold text-sm">📺 WATCH & LEARN</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('Understanding Quantum Healing', 'क्वांटम हीलिंग को समझें')}
            </h3>
            <p className="text-purple-200 text-lg max-w-2xl mx-auto">
              {t('Discover the science and power behind our revolutionary quantum healing technology', 'हमारी क्रांतिकारी क्वांटम हीलिंग तकनीक के पीछे का विज्ञान और शक्ति जानिए')}
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* Decorative glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 blur-3xl rounded-3xl"></div>
            
            {/* Video container */}
            <div className="relative bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur-xl border-2 border-purple-400/40 rounded-3xl p-4 shadow-2xl">
              <div className="relative" style={{paddingBottom: '56.25%', height: 0, overflow: 'hidden'}}>
                <iframe
                  src="https://www.youtube.com/embed/zyAmFNJfvY8"
                  title="Quantum Healing Explanation"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-2xl"
                  style={{border: 'none'}}
                  loading="lazy"
                ></iframe>
              </div>
            </div>

            {/* Video description */}
            <div className="mt-6 text-center">
              <p className="text-purple-200 text-sm">
                ✨ Learn how quantum physics is revolutionizing wellness and DNA healing ✨
              </p>
            </div>
          </div>
        </div>

        {/* Quantum Visual Section - Enhanced with more images */}
        <div className="mt-32 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-indigo-900/20 blur-3xl"></div>
          <div className="relative grid md:grid-cols-3 gap-6">
            <div className="relative overflow-hidden rounded-3xl border-2 border-purple-400/30 shadow-2xl group">
              <img 
                src="https://images.unsplash.com/photo-1621435410603-749c3094eb6c?w=600&q=50"
                alt="Quantum Energy Spheres"
                className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-purple-900/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <h4 className="text-xl font-bold text-white mb-2">Quantum Energy Field</h4>
                <p className="text-purple-200 text-sm">Pink & purple energy spheres</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-3xl border-2 border-purple-400/30 shadow-2xl group">
              <img 
                src="https://images.unsplash.com/photo-1635399860495-2a2802a6df5e?w=600&q=50"
                alt="Transformation Energy"
                className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-purple-900/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <h4 className="text-xl font-bold text-white mb-2">DNA-Level Transformation</h4>
                <p className="text-purple-200 text-sm">Cellular regeneration through quantum physics</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-3xl border-2 border-purple-400/30 shadow-2xl group">
              <img 
                src="https://customer-assets.emergentagent.com/job_healflow-4/artifacts/48kmyuw9_quantum%20img%202.jpg"
                alt="Atomic Healing Network"
                className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-purple-900/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <h4 className="text-xl font-bold text-white mb-2">Atomic Healing Network</h4>
                <p className="text-purple-200 text-sm">Advanced quantum technology</p>
              </div>
            </div>
          </div>

          {/* Additional row of images */}
          <div className="relative grid md:grid-cols-2 gap-6 mt-6">
            <div className="relative overflow-hidden rounded-3xl border-2 border-purple-400/30 shadow-2xl group">
              <img 
                src="https://images.pexels.com/photos/8728291/pexels-photo-8728291.jpeg?auto=compress&w=600&q=50"
                alt="Healing Meditation"
                className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-purple-900/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <h4 className="text-xl font-bold text-white mb-2">Spiritual Wellness</h4>
                <p className="text-purple-200 text-sm">Mind-body-soul harmony</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-3xl border-2 border-purple-400/30 shadow-2xl group">
              <img 
                src="https://images.pexels.com/photos/16124403/pexels-photo-16124403.jpeg?auto=compress&w=600&q=50"
                alt="Quantum Meditation"
                className="w-full h-80 object-cover transform group-hover:scale-110 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 via-purple-900/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                <h4 className="text-xl font-bold text-white mb-2">Energy Transformation</h4>
                <p className="text-purple-200 text-sm">Unlock your healing potential</p>
              </div>
            </div>
          </div>
        </div>

        {/* Goa Travelers Special Section */}
        <div className="mt-32 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-indigo-900/20 to-blue-900/20 blur-3xl"></div>
          
          <div className="relative bg-gradient-to-br from-blue-900/40 to-indigo-900/40 backdrop-blur-xl border-2 border-blue-400/40 rounded-3xl p-6 md:p-12 shadow-2xl">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-block bg-blue-500/20 px-6 py-2 rounded-full mb-6 border border-blue-400/40">
                <span className="text-blue-300 font-bold text-sm">SPECIAL FOR GOA TRAVELERS</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                Visiting Goa? We're Here to Help!
              </h3>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-6">
                Quick relief for health challenges during your Goa stay
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Left: Benefits */}
              <div className="space-y-6">
                <h4 className="text-2xl font-bold text-blue-200 mb-6">Common Travel Health Issues We Address:</h4>
                <div className="space-y-4">
                  {[
                    { icon: '🤒', title: 'Digestive Issues', desc: 'Food sensitivity, stomach upset, indigestion' },
                    { icon: '😰', title: 'Travel Fatigue', desc: 'Jetlag, exhaustion, low energy levels' },
                    { icon: '🤧', title: 'Minor Infections', desc: 'Cold, flu, fever, body aches' },
                    { icon: '😓', title: 'Stress & Anxiety', desc: 'Travel stress, panic attacks, insomnia' },
                    { icon: '🦟', title: 'Skin Issues', desc: 'Rashes, allergies, insect bites' },
                    { icon: '💊', title: 'Hangovers', desc: 'Post-party recovery, headaches' }
                  ].map((issue, idx) => (
                    <div key={idx} className="flex items-start space-x-4 bg-blue-950/30 p-4 rounded-xl border border-blue-400/20 hover:border-blue-400/40 transition-all">
                      <span className="text-3xl">{issue.icon}</span>
                      <div>
                        <p className="text-white font-semibold text-lg">{issue.title}</p>
                        <p className="text-blue-200 text-sm">{issue.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Why Choose Us */}
              <div>
                <h4 className="text-2xl font-bold text-blue-200 mb-6">Why Travelers Choose Divine Quanta:</h4>
                <div className="space-y-4 mb-8">
                  <div className="bg-blue-950/30 p-6 rounded-xl border border-blue-400/20">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-blue-500/20 p-3 rounded-full">
                        <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h5 className="text-white font-bold text-lg">Quick Relief</h5>
                    </div>
                    <p className="text-blue-200 text-sm">Fast-acting quantum healing - feel better in hours, not days</p>
                  </div>

                  <div className="bg-blue-950/30 p-6 rounded-xl border border-blue-400/20">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-blue-500/20 p-3 rounded-full">
                        <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <h5 className="text-white font-bold text-lg">Local in Goa</h5>
                    </div>
                    <p className="text-blue-200 text-sm">In-person sessions available - no need to cut short your vacation</p>
                  </div>

                  <div className="bg-blue-950/30 p-6 rounded-xl border border-blue-400/20">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-blue-500/20 p-3 rounded-full">
                        <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h5 className="text-white font-bold text-lg">Affordable</h5>
                    </div>
                    <p className="text-blue-200 text-sm">Special traveler rates - starting from Rs 1,500 per session</p>
                  </div>

                  <div className="bg-blue-950/30 p-6 rounded-xl border border-blue-400/20">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="bg-blue-500/20 p-3 rounded-full">
                        <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <h5 className="text-white font-bold text-lg">No Medication</h5>
                    </div>
                    <p className="text-blue-200 text-sm">100% natural healing - safe, non-invasive, no side effects</p>
                  </div>
                </div>

                {/* Booking Button */}
                <div className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 rounded-2xl p-6 border-2 border-blue-400/40">
                  <h5 className="text-white font-bold text-xl mb-3 text-center">Book Your Goa Session Now</h5>
                  <p className="text-blue-100 text-sm text-center mb-4">Get back to enjoying your vacation pain-free!</p>
                  <a 
                    href="https://wa.me/918920443069?text=Hi,%20I'm%20traveling%20in%20Goa%20and%20need%20help%20with%20minor%20health%20issues.%20Can%20you%20assist?"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-full font-bold text-lg text-center transition-all shadow-lg"
                    style={{boxShadow: '0 0 30px rgba(99, 102, 241, 0.4)'}}
                  >
                    <svg className="w-6 h-6 inline mr-2 mb-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Book Goa Session
                  </a>
                </div>
              </div>
            </div>

            {/* Testimonial */}
            <div className="bg-gradient-to-br from-indigo-900/30 to-blue-900/30 backdrop-blur-sm border-2 border-blue-400/30 rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <svg className="w-12 h-12 text-blue-300 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                </svg>
                <div>
                  <p className="text-white text-lg italic mb-4">
                    "I had severe food poisoning during my Goa trip. Within 4 hours of the quantum healing session, I was back to normal and enjoying the beach again! Saved my vacation!" 
                  </p>
                  <p className="text-blue-200 font-semibold">- Rahul M., Mumbai</p>
                  <p className="text-blue-300/70 text-sm">Goa Traveler, December 2024</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Distance Healing Revolution Section */}
        <div className="mt-32">
          <div className="bg-gradient-to-br from-cyan-900/40 to-blue-900/40 backdrop-blur-xl border-2 border-cyan-400/40 rounded-3xl p-6 md:p-12 shadow-2xl" style={{boxShadow: '0 0 60px rgba(34,211,238,0.1)'}}>
            <div className="text-center mb-12">
              <div className="inline-block bg-cyan-500/20 px-6 py-2 rounded-full mb-6 border border-cyan-400/40">
                <span className="text-cyan-300 font-bold text-sm">QUANTUM ENTANGLEMENT PROTOCOL</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('Non-Local Quantum Healing', 'दूरस्थ (नॉन-लोकल) क्वांटम हीलिंग')}
              </h3>
              <p className="text-2xl text-cyan-200 font-semibold mb-4">
                {t('Wave Function Collapse for Cellular Regeneration', 'कोशिकीय पुनर्जनन के लिए वेव फंक्शन कोलैप्स')}
              </p>
              <p className="text-lg text-cyan-100 max-w-3xl mx-auto">
                Based on Bell's Theorem and quantum non-locality - particles once entangled remain connected regardless of distance. We apply this principle to your bio-energetic field for healing at the subatomic level.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-cyan-950/30 backdrop-blur-sm border border-cyan-400/20 rounded-2xl p-6 text-center hover:scale-105 transition-all hover:border-cyan-400/50" style={{transition: 'all 0.3s ease'}}>
                <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-400/30">
                  <Globe className="w-8 h-8 text-cyan-300" />
                </div>
                <h4 className="text-xl font-bold text-cyan-300 mb-3">Non-Local Healing</h4>
                <p className="text-cyan-100 text-sm">
                  Quantum mechanics proves particles interact instantaneously across any distance. Your energy field responds to healing frequencies transmitted through quantum entanglement - no travel required.
                </p>
              </div>

              <div className="bg-cyan-950/30 backdrop-blur-sm border border-cyan-400/20 rounded-2xl p-6 text-center hover:scale-105 transition-all hover:border-cyan-400/50" style={{transition: 'all 0.3s ease'}}>
                <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-400/30">
                  <Atom className="w-8 h-8 text-cyan-300" />
                </div>
                <h4 className="text-xl font-bold text-cyan-300 mb-3">Subatomic DNA Repair</h4>
                <p className="text-cyan-100 text-sm">
                  Working at the quantum level (10<sup>-35</sup>m) - our technology targets damaged cellular structures, restoring DNA integrity through precise frequency modulation and quantum coherence.
                </p>
              </div>

              <div className="bg-cyan-950/30 backdrop-blur-sm border border-cyan-400/20 rounded-2xl p-6 text-center hover:scale-105 transition-all hover:border-cyan-400/50" style={{transition: 'all 0.3s ease'}}>
                <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-cyan-400/30">
                  <Zap className="w-8 h-8 text-cyan-300" />
                </div>
                <h4 className="text-xl font-bold text-cyan-300 mb-3">Quantum Field Resonance</h4>
                <p className="text-cyan-100 text-sm">
                  Using the Schumann resonance (7.83Hz) as a carrier wave, we align your bio-field with Earth's natural electromagnetic frequency for optimal healing coherence.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-cyan-500/20 rounded-2xl p-8 border border-cyan-400/30">
              <h4 className="text-2xl font-bold text-white mb-4 text-center">{t('How Distance Healing Works', 'डिस्टेंस हीलिंग कैसे काम करती है')}</h4>
              <div className="grid md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="bg-cyan-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 font-bold text-xl">1</div>
                  <p className="text-cyan-100 text-sm font-semibold mb-2">Book Your Session</p>
                  <p className="text-cyan-200 text-xs">Schedule online or via WhatsApp</p>
                </div>
                <div>
                  <div className="bg-cyan-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 font-bold text-xl">2</div>
                  <p className="text-cyan-100 text-sm font-semibold mb-2">Share Your Details</p>
                  <p className="text-cyan-200 text-xs">Provide basic information remotely</p>
                </div>
                <div>
                  <div className="bg-cyan-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 font-bold text-xl">3</div>
                  <p className="text-cyan-100 text-sm font-semibold mb-2">Quantum Connection</p>
                  <p className="text-cyan-200 text-xs">We connect to your energy field</p>
                </div>
                <div>
                  <div className="bg-cyan-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3 font-bold text-xl">4</div>
                  <p className="text-cyan-100 text-sm font-semibold mb-2">Experience Healing</p>
                  <p className="text-cyan-200 text-xs">Feel transformation from home</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* What is Quantum Wellness Section */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <div className="inline-block bg-purple-500/20 px-6 py-2 rounded-full mb-6 border border-purple-400/40">
              <span className="text-purple-300 font-bold text-sm">THE SCIENCE BEHIND THE HEALING</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              Quantum Physics Meets Wellness
            </h3>
            <div className="max-w-4xl mx-auto space-y-6 text-purple-200 text-lg leading-relaxed">
              <p>
                At the subatomic level, everything in the universe is <strong className="text-cyan-300">interconnected through quantum fields</strong>. The observer effect in quantum mechanics demonstrates that consciousness directly influences matter. Your cells - comprised of atoms governed by quantum laws - respond to <strong className="text-purple-300">targeted frequency modulation</strong> and <strong className="text-pink-300">coherent energy patterns</strong>.
              </p>
              <p>
                Divine Quanta uses advanced <strong className="text-cyan-300">German-engineered quantum resonance technology</strong> to measure your body's bio-energetic field - your <strong className="text-purple-300">Prana or life force</strong> - detecting imbalances at the quantum level that manifest as physical, emotional, or spiritual distress.
              </p>

              {/* Quantum Equation Display */}
              <div className="bg-black/40 border border-purple-400/30 rounded-2xl p-6 my-8 backdrop-blur-xl">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
                  <div className="bg-purple-500/10 border border-purple-400/20 rounded-xl p-4 flex-1">
                    <p className="text-cyan-300 text-xl font-mono mb-1">E = hf</p>
                    <p className="text-purple-300 text-xs">Energy-Frequency Relation</p>
                  </div>
                  <div className="text-purple-400 text-2xl">+</div>
                  <div className="bg-purple-500/10 border border-purple-400/20 rounded-xl p-4 flex-1">
                    <p className="text-cyan-300 text-xl font-mono mb-1">|&psi;&rangle; = &alpha;|0&rangle; + &beta;|1&rangle;</p>
                    <p className="text-purple-300 text-xs">Quantum Superposition</p>
                  </div>
                  <div className="text-purple-400 text-2xl">=</div>
                  <div className="bg-gradient-to-br from-purple-500/20 to-cyan-500/20 border border-cyan-400/30 rounded-xl p-4 flex-1">
                    <p className="text-white text-xl font-bold mb-1">Healing</p>
                    <p className="text-cyan-300 text-xs">Quantum Coherent State</p>
                  </div>
                </div>
              </div>

              <p className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-purple-300 to-pink-300">
                True healing occurs at the intersection of consciousness and quantum fields - where wave functions collapse into observable wellness transformation.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur-xl border border-purple-400/30 rounded-3xl p-6 md:p-12" style={{boxShadow: '0 0 40px rgba(168,85,247,0.1)'}}>
            <h4 className="text-3xl font-bold text-white mb-8 text-center" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('Why Quantum Healing Surpasses Traditional Methods', 'क्वांटम हीलिंग पारंपरिक तरीकों से बेहतर क्यों है')}
            </h4>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-600/30 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Brain className="w-6 h-6 text-purple-300" />
                  </div>
                  <div>
                    <h5 className="text-xl font-bold text-white mb-2">Deep Cellular & DNA Level Healing</h5>
                    <p className="text-purple-200">
                      Unlike traditional methods that focus on symptoms, Quantum Healing reaches deep into your energy body and subconscious, targeting the actual source of imbalance—whether it's trauma, stress, karmic patterns, or ancestral memory.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h5 className="text-xl font-bold text-white mb-2">Non-Invasive & Pain-Free</h5>
                    <p className="text-purple-200">
                      No pills. No needles. No side effects. Safe for people of all ages and health conditions. Each session is tailored specifically to your emotional patterns, life story, and energetic blueprint.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-indigo-600/30 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Star className="w-6 h-6 text-indigo-300" />
                  </div>
                  <div>
                    <h5 className="text-xl font-bold text-white mb-2">Fusion of Ancient Wisdom & Modern Science</h5>
                    <p className="text-purple-200">
                      We combine Osho's meditative presence, Buddhist mindfulness, and German quantum wellness systems. This unique integration bridges ancient spiritual insight with cutting-edge vibrational technology.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-purple-600/30 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Sparkles className="w-6 h-6 text-purple-300" />
                  </div>
                  <div>
                    <h5 className="text-xl font-bold text-white mb-2">Accessible Worldwide—Remote Healing</h5>
                    <p className="text-purple-200">
                      Distance is no barrier when healing is done through pure consciousness and quantum entanglement. Receive powerful healing sessions from anywhere in the world, transmitted energetically to your cellular matrix.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('How Quantum Healing Works', 'क्वांटम हीलिंग कैसे काम करती है')}
            </h3>
            <p className="text-lg text-purple-200 max-w-3xl mx-auto">
              {t('Our scientific approach combines quantum sensors with energetic healing to create profound transformation at every level of your being.',
                'हमारा वैज्ञानिक दृष्टिकोण क्वांटम सेंसर और ऊर्जा उपचार को जोड़कर आपके अस्तित्व के हर स्तर पर गहन परिवर्तन लाता है।')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-purple-900/30 backdrop-blur-lg border border-purple-500/30 rounded-2xl p-8 hover:bg-purple-900/40 transition-colors text-center">
              <div className="bg-purple-600/30 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto text-2xl font-bold text-purple-300">
                1
              </div>
              <h4 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>Prana Field Scanning</h4>
              <p className="text-purple-200">
                Using advanced German quantum sensors, we record and analyze your Pranic field—your energetic blueprint—to identify imbalances, blockages, and areas needing attention.
              </p>
            </div>

            <div className="bg-purple-900/30 backdrop-blur-lg border border-purple-500/30 rounded-2xl p-8 hover:bg-purple-900/40 transition-colors text-center">
              <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto text-2xl font-bold text-white">
                2
              </div>
              <h4 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>Energy Transmission</h4>
              <p className="text-purple-200">
                Through quantum entanglement, customized healing frequencies are transmitted remotely to your cellular and energetic matrix, working on physical, emotional, and spiritual levels.
              </p>
            </div>

            <div className="bg-purple-900/30 backdrop-blur-lg border border-purple-500/30 rounded-2xl p-8 hover:bg-purple-900/40 transition-colors text-center">
              <div className="bg-indigo-600/30 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto text-2xl font-bold text-indigo-300">
                3
              </div>
              <h4 className="text-xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>Transformation & Integration</h4>
              <p className="text-purple-200">
                Experience profound shifts in mental clarity, emotional balance, and physical vitality as your body repairs itself and releases long-held trauma over several days to weeks.
              </p>
            </div>
          </div>
        </div>

        {/* Prana Analysis - Mandatory First Step */}
        <div className="mt-32">
          <div className="bg-gradient-to-br from-white/10 to-purple-900/40 backdrop-blur-xl border-2 border-white/30 rounded-3xl p-6 md:p-12 spiritual-glow">
            <div className="text-center mb-8">
              <div className="inline-block bg-white/20 px-6 py-2 rounded-full mb-4">
                <span className="text-white font-bold text-sm">{t('STEP 1 - MANDATORY', 'चरण 1 — अनिवार्य')}</span>
              </div>
              <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {t('Prana Analysis Reading', 'प्राण विश्लेषण रीडिंग')}
              </h3>
              <p className="text-xl text-purple-100 max-w-3xl mx-auto mb-8">
                {t('Every healing journey begins with a comprehensive Prana Analysis. Using advanced German quantum sensors, we map your energetic blueprint to identify imbalances and blockages.',
                  'हर उपचार यात्रा एक व्यापक प्राण विश्लेषण से शुरू होती है। उन्नत जर्मन क्वांटम सेंसर से हम आपकी ऊर्जा संरचना का मानचित्रण कर असंतुलन और अवरोधों की पहचान करते हैं।')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-purple-900/40 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <h4 className="text-2xl font-bold text-white mb-4">{t('What You Receive:', 'आपको क्या मिलेगा:')}</h4>
                <ul className="space-y-3 text-purple-100">
                  <li className="flex items-start">
                    <span className="text-white mr-2">✓</span>
                    {t('Complete Pranic field scanning using quantum technology', 'क्वांटम तकनीक से संपूर्ण प्राणिक फील्ड स्कैनिंग')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-white mr-2">✓</span>
                    {t('Detailed Aura Report identifying energy blockages', 'ऊर्जा अवरोधों की पहचान करती विस्तृत आभा रिपोर्ट')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-white mr-2">✓</span>
                    {t('Personalized report sent directly to your WhatsApp', 'व्यक्तिगत रिपोर्ट सीधे आपके व्हाट्सएप पर')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-white mr-2">✓</span>
                    {t('Chakra assessment and imbalance detection', 'चक्र मूल्यांकन और असंतुलन की पहचान')}
                  </li>
                  <li className="flex items-start">
                    <span className="text-white mr-2">✓</span>
                    {t('Therapist consultation to review your results', 'परिणामों की समीक्षा हेतु थेरेपिस्ट परामर्श')}
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-700/50 to-indigo-800/50 backdrop-blur-lg border-2 border-white/30 rounded-2xl p-8 flex flex-col items-center justify-center text-center spiritual-glow">
                <div className="mb-4">
                  <p className="text-purple-100 text-lg mb-2">{t('Investment', 'निवेश')}</p>
                  <div className="text-6xl font-bold text-white mb-2">₹1,500</div>
                  <p className="text-purple-100 text-sm">{t('One-time analysis fee', 'एक बार का विश्लेषण शुल्क')}</p>
                </div>
                <div className="w-full h-px bg-white/20 my-6"></div>
                <p className="text-white font-semibold text-lg">
                  {t('Start Your Healing Journey Here', 'यहीं से शुरू करें अपनी उपचार यात्रा')}
                </p>
              </div>
            </div>

            <div className="bg-indigo-900/30 backdrop-blur-lg border border-indigo-400/30 rounded-xl p-6">
              <h4 className="text-white font-bold text-lg mb-3 text-center">How It Works:</h4>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-xl">1</div>
                  <p className="text-purple-100 text-sm">Book Prana Analysis (₹1,500)</p>
                </div>
                <div>
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-xl">2</div>
                  <p className="text-purple-100 text-sm">Receive detailed Aura Report on WhatsApp</p>
                </div>
                <div>
                  <div className="bg-white/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-xl">3</div>
                  <p className="text-purple-100 text-sm">Therapist recommends healing duration based on your analysis</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Reports & Reading */}
        <div className="mt-32" data-testid="analysis-reports-section">
          <div className="text-center mb-12">
            <div className="inline-block bg-purple-500/30 text-purple-200 px-6 py-2 rounded-full text-sm backdrop-blur-sm border border-purple-400/30 mb-6">
              STEP 1.5 — {t('DETAILED REPORTS', 'विस्तृत रिपोर्ट्स')}
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('Analysis Reports & Reading', 'विश्लेषण रिपोर्ट और रीडिंग')}
            </h3>
            <p className="text-lg text-purple-200 max-w-3xl mx-auto">
              {t(<>Deep-dive reports delivered to your WhatsApp. <span className="text-amber-300 font-semibold">Divine Quanta Members enjoy 20% off all reports.</span></>,
                <>गहन रिपोर्ट सीधे आपके व्हाट्सएप पर। <span className="text-amber-300 font-semibold">डिवाइन क्वांटा सदस्यों को सभी रिपोर्ट्स पर 20% छूट।</span></>)}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Aura Analysis */}
            <div className="bg-gradient-to-br from-purple-800/40 to-indigo-900/40 backdrop-blur-lg border border-purple-400/30 rounded-2xl p-7 flex flex-col" data-testid="report-aura-card">
              <div className="text-4xl mb-3">🔮</div>
              <h4 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('Aura Analysis', 'आभा (औरा) विश्लेषण')}</h4>
              <p className="text-purple-200 text-sm mb-5">{t('Complete energetic field scan revealing colour, intensity and imbalances of your aura.', 'आपकी आभा का रंग, तीव्रता और असंतुलन उजागर करता संपूर्ण ऊर्जा क्षेत्र स्कैन।')}</p>
              <div className="space-y-1 mb-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-white">₹2,000</span>
                  <span className="text-xs text-purple-300">non-member</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-amber-300">₹1,600</span>
                  <span className="text-xs text-amber-200/80">Member price · save ₹400</span>
                </div>
              </div>
              <ul className="space-y-2 text-purple-200 text-sm mb-6">
                <li>✓ Layered aura colour mapping</li>
                <li>✓ Energy blockage identification</li>
                <li>✓ Personalized PDF on WhatsApp</li>
              </ul>
              <a
                href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20an%20Aura%20Analysis%20(₹2,000%20%7C%20Members%20₹1,600).%20Please%20send%20me%20the%20report%20on%20WhatsApp."
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-full text-sm font-semibold transition-all"
                data-testid="report-aura-cta"
              >
                {t('Book Aura Report', 'औरा रिपोर्ट बुक करें')}
              </a>
            </div>

            {/* Chakra Analysis */}
            <div className="bg-gradient-to-br from-violet-800/40 to-purple-900/40 backdrop-blur-lg border border-violet-400/30 rounded-2xl p-7 flex flex-col" data-testid="report-chakra-card">
              <div className="text-4xl mb-3">⚡</div>
              <h4 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>Chakra Analysis</h4>
              <p className="text-violet-200 text-sm mb-5">Seven-chakra alignment scan with detailed balancing recommendations.</p>
              <div className="space-y-1 mb-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-white">₹2,000</span>
                  <span className="text-xs text-violet-300">non-member</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-amber-300">₹1,600</span>
                  <span className="text-xs text-amber-200/80">Member price · save ₹400</span>
                </div>
              </div>
              <ul className="space-y-2 text-violet-200 text-sm mb-6">
                <li>✓ All 7 chakras evaluated</li>
                <li>✓ Imbalance & overactivity flags</li>
                <li>✓ Personalized PDF on WhatsApp</li>
              </ul>
              <a
                href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20a%20Chakra%20Analysis%20(₹2,000%20%7C%20Members%20₹1,600).%20Please%20send%20me%20the%20report%20on%20WhatsApp."
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-full text-sm font-semibold transition-all"
                data-testid="report-chakra-cta"
              >
                {t('Book Chakra Report', 'चक्र रिपोर्ट बुक करें')}
              </a>
            </div>

            {/* Full Analysis Report (Personal Detailed) */}
            <div className="bg-gradient-to-br from-amber-700/40 to-purple-900/40 backdrop-blur-lg border-2 border-amber-400/40 rounded-2xl p-7 flex flex-col relative spiritual-glow" data-testid="report-full-card">
              <div className="absolute -top-3 left-4 bg-white text-purple-900 text-xs px-3 py-1 rounded-full font-bold">MOST DETAILED</div>
              <div className="text-4xl mb-3">📊</div>
              <h4 className="text-2xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('Full Analysis Report', 'संपूर्ण विश्लेषण रिपोर्ट')}</h4>
              <p className="text-amber-100 text-sm mb-5">{t('Personal detailed report combining aura, chakra, organ, emotional and pranic mapping.', 'आभा, चक्र, अंग, भावनात्मक और प्राणिक मानचित्रण को जोड़ती व्यक्तिगत विस्तृत रिपोर्ट।')}</p>
              <div className="space-y-1 mb-5">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-white">₹3,000</span>
                  <span className="text-xs text-amber-100">non-member</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-amber-300">₹2,400</span>
                  <span className="text-xs text-amber-200/80">Member price · save ₹600</span>
                </div>
              </div>
              <ul className="space-y-2 text-amber-100 text-sm mb-6">
                <li>✓ Full Aura + Chakra mapping</li>
                <li>✓ Organ & cellular stress scan</li>
                <li>✓ Emotional/trauma blueprint</li>
                <li>✓ Custom healing roadmap</li>
              </ul>
              <a
                href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20the%20Full%20Analysis%20Report%20-%20Personal%20Detailed%20(₹3,000%20%7C%20Members%20₹2,400).%20Please%20send%20me%20the%20report%20on%20WhatsApp."
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-600 to-purple-600 hover:from-amber-700 hover:to-purple-700 text-white px-6 py-3 rounded-full text-sm font-semibold transition-all"
                data-testid="report-full-cta"
              >
                {t('Book Full Report', 'पूरी रिपोर्ट बुक करें')}
              </a>
            </div>
          </div>
        </div>

        {/* Session Packages */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <div className="inline-block bg-purple-500/30 text-purple-200 px-6 py-2 rounded-full text-sm backdrop-blur-sm border border-purple-400/30 mb-6">
              STEP 2 - {t('AFTER PRANA ANALYSIS', 'प्राण विश्लेषण के बाद')}
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('Quantum Healing Sessions', 'क्वांटम हीलिंग सत्र')}
            </h3>
            <p className="text-lg text-purple-200 max-w-3xl mx-auto mb-4">
              {t('Based on your Prana Analysis results, our therapist will recommend the optimal number of healing days required for your specific condition. Below are our typical healing packages:',
                'आपके प्राण विश्लेषण के परिणामों के आधार पर, हमारी थेरेपिस्ट आपकी स्थिति के लिए आवश्यक उपचार दिवसों की सलाह देंगी। नीचे हमारे सामान्य हीलिंग पैकेज दिए गए हैं:')}
            </p>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4 max-w-2xl mx-auto">
              <p className="text-purple-100 text-sm">
                {t(<><strong className="text-white">Note:</strong> The exact number of healing sessions will be determined after reviewing your Prana Analysis and Aura Report. These packages serve as a general guide.</>,
                  <><strong className="text-white">नोट:</strong> उपचार सत्रों की सटीक संख्या आपके प्राण विश्लेषण और आभा रिपोर्ट की समीक्षा के बाद तय होगी। ये पैकेज एक सामान्य मार्गदर्शिका हैं।</>)}
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-purple-800/40 to-purple-900/40 backdrop-blur-lg border border-purple-500/30 rounded-2xl p-6" data-testid="package-single">
              <h4 className="text-xl font-bold text-white mb-2">{t('Single Session', 'एकल सत्र')}</h4>
              <div className="text-3xl font-bold text-purple-300 mb-4">₹2,500</div>
              <p className="text-purple-200 text-sm mb-4">{t('Perfect for first-time seekers', 'पहली बार आने वालों के लिए उत्तम')}</p>
              <ul className="space-y-2 text-purple-200 text-sm">
                <li>✓ {t('1 Healing Session', '1 हीलिंग सत्र')}</li>
                <li>✓ {t('Energy Assessment', 'ऊर्जा मूल्यांकन')}</li>
                <li>✓ {t('Personalized Guidance', 'व्यक्तिगत मार्गदर्शन')}</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-700/40 to-indigo-900/40 backdrop-blur-lg border border-purple-400/40 rounded-2xl p-6 relative" data-testid="package-3">
              <div className="absolute -top-3 right-4 bg-purple-500 text-white text-xs px-3 py-1 rounded-full">Save 13%</div>
              <h4 className="text-xl font-bold text-white mb-2">{t('3 Sessions', '3 सत्र')}</h4>
              <div className="text-3xl font-bold text-white mb-4">₹6,500</div>
              <p className="text-purple-200 text-sm mb-4">{t('Begin your transformation', 'अपना परिवर्तन शुरू करें')}</p>
              <ul className="space-y-2 text-purple-200 text-sm">
                <li>✓ {t('3 Healing Sessions', '3 हीलिंग सत्र')}</li>
                <li>✓ {t('Progress Tracking', 'प्रगति ट्रैकिंग')}</li>
                <li>✓ {t('WhatsApp Support', 'व्हाट्सएप सहायता')}</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-purple-600/60 to-indigo-900/60 backdrop-blur-lg border-2 border-white/40 rounded-2xl p-6 relative spiritual-glow" data-testid="package-5">
              <div className="absolute -top-3 right-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs px-3 py-1 rounded-full">Save 20%</div>
              <div className="absolute -top-3 left-4 bg-white text-purple-900 text-xs px-3 py-1 rounded-full font-bold">POPULAR</div>
              <h4 className="text-xl font-bold text-white mb-2">{t('5 Sessions', '5 सत्र')}</h4>
              <div className="text-3xl font-bold text-white mb-4">₹10,000</div>
              <p className="text-purple-100 text-sm mb-4">{t('Deep healing journey', 'गहन उपचार यात्रा')}</p>
              <ul className="space-y-2 text-purple-100 text-sm">
                <li>✓ {t('5 Healing Sessions', '5 हीलिंग सत्र')}</li>
                <li>✓ {t('Priority Booking', 'प्राथमिकता बुकिंग')}</li>
                <li>✓ {t('Extended Support', 'विस्तारित सहायता')}</li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-indigo-700/40 to-purple-900/40 backdrop-blur-lg border border-purple-400/30 rounded-2xl p-6 relative" data-testid="package-10">
              <div className="absolute -top-3 right-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs px-3 py-1 rounded-full">Save 28%</div>
              <h4 className="text-xl font-bold text-white mb-2">10 Sessions</h4>
              <div className="text-3xl font-bold text-purple-300 mb-4">₹18,000</div>
              <p className="text-purple-200 text-sm mb-4">Complete transformation</p>
              <ul className="space-y-2 text-purple-200 text-sm">
                <li>✓ 10 Healing Sessions</li>
                <li>✓ VIP Support</li>
                <li>✓ Lifetime Guidance</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Corporate Wellness — Detailed Section */}
        <div className="mt-32" id="corporate-section" data-testid="corporate-section">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-cyan-900/40 to-indigo-900/30 blur-3xl"></div>

            <div className="relative bg-gradient-to-br from-slate-900/80 via-blue-950/80 to-indigo-950/80 backdrop-blur-xl border-2 border-blue-400/30 rounded-3xl p-8 md:p-12 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-block bg-blue-500/20 px-6 py-2 rounded-full mb-6 border border-blue-400/40">
                  <span className="text-blue-300 font-bold text-sm">FOR CORPORATES</span>
                </div>
                <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                  Install Quantum Science <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-300 to-indigo-300">Inside Your Organization</span>
                </h3>
                <p className="text-lg text-blue-100 max-w-3xl mx-auto">
                  Boost job productivity, department growth &amp; staff mental health with a science-based wellness program — delivered live over Zoom &amp; Google Meet.
                </p>
              </div>

              {/* Science Explainer */}
              <div className="max-w-4xl mx-auto mb-10 bg-gradient-to-r from-blue-900/40 via-cyan-900/40 to-indigo-900/40 border border-blue-400/25 rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-yellow-400/20 border border-yellow-300/40 p-2 rounded-lg">
                    <svg className="w-5 h-5 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                    Educate. Install. Transform.
                  </h4>
                </div>
                <p className="text-blue-100 leading-relaxed text-base">
                  We come to your organization — <strong className="text-yellow-200">live over Zoom or Google Meet</strong> — and educate your leadership, HR and teams on
                  <strong className="text-yellow-200"> how quantum physics can transform workplace performance</strong>. We then install the science into daily
                  routines: <strong className="text-yellow-200">focus, motivation, emotional balance, and mental health support</strong> — so your people work
                  clearer, calmer and stronger every day.
                </p>
              </div>

              {/* What we work on — 4 pillars */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                {[
                  {icon: '📈', title: t('Job Productivity', 'कार्य उत्पादकता'), desc: t('Focus, energy & clarity to help staff perform at their peak every day', 'फोकस, ऊर्जा और स्पष्टता — ताकि कर्मचारी हर दिन अपना सर्वश्रेष्ठ दें')},
                  {icon: '🏢', title: t('Department Growth', 'विभागीय विकास'), desc: t('Aligned teams, better collaboration & measurable performance lift', 'संरेखित टीमें, बेहतर सहयोग और मापने योग्य प्रदर्शन वृद्धि')},
                  {icon: '🧠', title: t('Staff Mental Health', 'कर्मचारी मानसिक स्वास्थ्य'), desc: t('Stress, burnout, anxiety & emotional overload addressed at the root', 'तनाव, बर्नआउट, चिंता और भावनात्मक बोझ का जड़ से समाधान')},
                  {icon: '💡', title: t('Overall Growth', 'समग्र विकास'), desc: t('Leadership clarity, decision-making sharpness & culture transformation', 'नेतृत्व स्पष्टता, निर्णय क्षमता और कार्य-संस्कृति परिवर्तन')},
                ].map((item, i) => (
                  <div key={i} className="bg-gradient-to-br from-blue-800/40 to-indigo-900/40 border border-blue-400/25 rounded-2xl p-5 hover:scale-105 transition-all">
                    <div className="text-3xl mb-2">{item.icon}</div>
                    <h5 className="text-white font-bold mb-2">{item.title}</h5>
                    <p className="text-blue-200 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Two-column: Educate / Install */}
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                <div className="bg-blue-900/30 border border-blue-400/25 rounded-2xl p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-500/25 p-3 rounded-xl">
                      <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-white">Educate via Zoom / Google Meet</h4>
                  </div>
                  <p className="text-blue-100 text-sm mb-4">
                    Live 45–60 minute educational sessions for your teams explaining the science and how it works.
                  </p>
                  <ul className="space-y-2 text-sm">
                    {[
                      'For leadership, HR &amp; employees',
                      'Understanding quantum wellness at work',
                      'Case studies &amp; measurable results',
                      'Live Q&amp;A with our therapist',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-blue-100">
                        <span className="text-blue-400 mt-1">✓</span>
                        <span dangerouslySetInnerHTML={{__html: item}} />
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-cyan-900/30 border border-cyan-400/25 rounded-2xl p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-cyan-500/25 p-3 rounded-xl">
                      <svg className="w-6 h-6 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-white">Install the Science</h4>
                  </div>
                  <p className="text-cyan-100 text-sm mb-4">
                    Ongoing quantum wellness programs installed into your team&apos;s weekly / monthly routine.
                  </p>
                  <ul className="space-y-2 text-sm">
                    {[
                      'Weekly distance quantum sessions',
                      'Individual assessments for staff',
                      'Manager clarity &amp; leadership support',
                      'Confidential 1-on-1 counselling',
                      'Impact reports every quarter',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-cyan-100">
                        <span className="text-cyan-400 mt-1">✓</span>
                        <span dangerouslySetInnerHTML={{__html: item}} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Big CTA */}
              <div className="bg-gradient-to-r from-blue-600/20 via-cyan-600/20 to-indigo-600/20 border-2 border-blue-400/30 rounded-2xl p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex-1">
                    <h5 className="text-2xl font-bold text-white mb-2">{t('Ready to bring quantum wellness to your organization?', 'अपने संगठन में क्वांटम वेलनेस लाने के लिए तैयार हैं?')}</h5>
                    <p className="text-blue-100 text-sm">
                      {t(<>Book a free 30-minute Zoom / Google Meet orientation for your leadership &amp; HR team. We&apos;ll walk through the science, results and a customised installation plan for your organization.</>,
                        'अपनी लीडरशिप और HR टीम के लिए 30 मिनट का निःशुल्क ज़ूम / गूगल मीट सत्र बुक करें। हम विज्ञान, परिणाम और आपके संगठन के लिए अनुकूलित योजना समझाएँगे।')}
                    </p>
                  </div>
                  <a
                    href="https://wa.me/918920443069?text=Hi%2C%20I%20represent%20a%20company%20and%20I%27d%20like%20to%20explore%20Quantum%20Wellness%20installation%20via%20Zoom%2FGoogle%20Meet%20for%20our%20staff%20on%20productivity%2C%20department%20growth%20and%20mental%20health.%20Please%20share%20details."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 flex items-center gap-2"
                    data-testid="corporate-book-zoom-btn"
                    style={{boxShadow: '0 0 25px rgba(59,130,246,0.35)'}}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {t('Book Corporate Zoom', 'कॉर्पोरेट ज़ूम बुक करें')}
                  </a>
                </div>
              </div>

              {/* Discovery Call — Book a Free 30-min slot */}
              <div className="mt-6 bg-gradient-to-r from-purple-900/40 via-indigo-900/40 to-purple-900/40 border-2 border-purple-400/30 rounded-2xl p-6 md:p-7" data-testid="corporate-discovery-call">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500/40 to-indigo-500/40 border border-purple-400/50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-block bg-purple-500/20 px-3 py-1 rounded-full border border-purple-400/40 mb-2">
                      <span className="text-purple-200 font-bold text-xs tracking-widest">{t('BOOK A DISCOVERY CALL', 'डिस्कवरी कॉल बुक करें')}</span>
                    </div>
                    <h5 className="text-xl md:text-2xl font-bold text-white mb-1">{t('Free 30-min Zoom / Google Meet', 'निःशुल्क 30-मिनट ज़ूम / गूगल मीट')}</h5>
                    <p className="text-purple-200 text-sm">{t(<>Message us on WhatsApp and we&apos;ll confirm your slot within a few hours — no cost, no commitment. Learn how quantum wellness fits your organization.</>,
                      'हमें व्हाट्सएप पर संदेश भेजें, कुछ ही घंटों में स्लॉट कन्फर्म होगा — न कोई शुल्क, न कोई बाध्यता। जानिए क्वांटम वेलनेस आपके संगठन के लिए कैसे उपयोगी है।')}</p>
                  </div>
                  <a
                    href="https://wa.me/918920443069?text=Hello%20Divine%20Quanta%20Team%2C%0A%0AI%27d%20like%20to%20book%20a%20*Free%2030-min%20Discovery%20Call*%20for%20our%20*organisation*%20to%20explore%20Quantum%20Wellness%20installation%20via%20Zoom%20%2F%20Google%20Meet.%0A%0AKindly%20share%20the%20next%20available%20slots.%0A%0AThank%20you.%20%F0%9F%99%8F"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap bg-white text-purple-900 hover:bg-purple-50 px-6 py-3 rounded-full font-bold transition-all hover:scale-105 shadow-lg flex items-center gap-2"
                    data-testid="corporate-discovery-cta"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {t('Request a Slot', 'स्लॉट का अनुरोध करें')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quantum Healing for Schools Section */}
        <div className="mt-32" data-testid="schools-section">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 via-teal-900/40 to-cyan-900/30 blur-3xl"></div>

            <div className="relative bg-gradient-to-br from-slate-900/80 via-teal-950/80 to-emerald-950/80 backdrop-blur-xl border-2 border-emerald-400/30 rounded-3xl p-8 md:p-12 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-block bg-emerald-500/20 px-6 py-2 rounded-full mb-6 border border-emerald-400/40">
                  <span className="text-emerald-300 font-bold text-sm">{t(<>FOR SCHOOLS &amp; EDUCATORS</>, 'स्कूलों और शिक्षकों के लिए')}</span>
                </div>
                <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                  {t(<>Bring Quantum Science <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300">Into Your Counseling Rooms</span></>,
                    <>अपने काउंसलिंग रूम में <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300">क्वांटम विज्ञान लाएँ</span></>)}
                </h3>
                <p className="text-lg text-emerald-100 max-w-3xl mx-auto">
                  {t('Empower your school counselors, teachers and children with the healing science of quantum physics — delivered through live Zoom sessions and distance therapy for students.',
                    'अपने स्कूल के काउंसलरों, शिक्षकों और बच्चों को क्वांटम भौतिकी के उपचार विज्ञान से सशक्त बनाएँ — लाइव ज़ूम सत्रों और विद्यार्थियों के लिए डिस्टेंस थेरेपी के माध्यम से।')}
                </p>
              </div>

              {/* The Science Behind It */}
              <div className="max-w-4xl mx-auto mb-10 bg-gradient-to-r from-emerald-900/40 via-teal-900/40 to-cyan-900/40 border border-emerald-400/25 rounded-2xl p-6 md:p-8" data-testid="schools-science-box">
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-yellow-400/20 border border-yellow-300/40 p-2 rounded-lg">
                    <svg className="w-5 h-5 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h4 className="text-xl md:text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                    A New Revolution in Mental Health — Backed by Science
                  </h4>
                </div>
                <p className="text-emerald-100 leading-relaxed text-base">
                  This therapy is built on <strong className="text-yellow-200">quantum physics programs</strong> specifically designed to work on
                  <strong className="text-yellow-200"> emotions, the subconscious mind, and inner-child healing</strong>. It gently uncovers the
                  emotional roots behind anxiety, focus issues, low self-worth and behavioural blocks in children — and quietly rewires them
                  at an energetic level. <strong className="text-yellow-200">Countless parents have already used it personally with their own
                  children</strong> and witnessed measurable, lasting change. It represents a
                  <strong className="text-yellow-200"> new revolution in mental health</strong> — where scientific therapy meets true inner transformation.
                </p>
              </div>

              {/* Two-column: What we offer / Who benefits */}
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                {/* Zoom Awareness Sessions */}
                <div className="bg-emerald-900/30 border border-emerald-400/25 rounded-2xl p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-emerald-500/25 p-3 rounded-xl">
                      <svg className="w-6 h-6 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-white">Live Zoom Sessions</h4>
                  </div>
                  <p className="text-emerald-100 text-sm mb-5 leading-relaxed">
                    Interactive sessions with school counselors, teachers and parents explaining the science of quantum healing and how it complements traditional counseling.
                  </p>
                  <ul className="space-y-2 text-sm">
                    {[
                      'Understanding quantum healing for children',
                      'How to identify emotional & energy blocks in students',
                      'Setting up a quantum wellness corner in school',
                      'Q&A with our certified therapist',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-emerald-100">
                        <span className="text-emerald-400 mt-1">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Quantum Therapy for Kids */}
                <div className="bg-teal-900/30 border border-teal-400/25 rounded-2xl p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-teal-500/25 p-3 rounded-xl">
                      <svg className="w-6 h-6 text-teal-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-white">Quantum Therapy for Kids</h4>
                  </div>
                  <p className="text-teal-100 text-sm mb-5 leading-relaxed">
                    Gentle, non-invasive distance quantum sessions delivered right in the school counseling room — no medication, no needles, just science-backed energy realignment.
                  </p>
                  <ul className="space-y-2 text-sm">
                    {[
                      'Exam stress &amp; performance anxiety',
                      'Focus, attention & ADHD support',
                      'Bullying trauma & emotional recovery',
                      'Sleep, mood & behavioural balance',
                      'Confidence & self-worth healing',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-teal-100">
                        <span className="text-teal-400 mt-1">✓</span>
                        <span dangerouslySetInnerHTML={{__html: item}} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Beneficiary tiles */}
              <div className="grid md:grid-cols-3 gap-4 mb-10">
                <div className="bg-gradient-to-br from-emerald-800/40 to-teal-900/40 border border-emerald-400/20 rounded-2xl p-5 text-center">
                  <div className="text-3xl mb-2">👧</div>
                  <h5 className="text-white font-bold mb-1">{t('For Students', 'विद्यार्थियों के लिए')}</h5>
                  <p className="text-emerald-200 text-xs">{t('Age-appropriate healing for children 6–18', '6–18 वर्ष के बच्चों के लिए आयु-उपयुक्त हीलिंग')}</p>
                </div>
                <div className="bg-gradient-to-br from-teal-800/40 to-cyan-900/40 border border-teal-400/20 rounded-2xl p-5 text-center">
                  <div className="text-3xl mb-2">🧑‍🏫</div>
                  <h5 className="text-white font-bold mb-1">{t(<>For Counselors &amp; Teachers</>, 'काउंसलरों और शिक्षकों के लिए')}</h5>
                  <p className="text-teal-200 text-xs">{t('Training + tools to support students better', 'विद्यार्थियों को बेहतर सहयोग देने हेतु प्रशिक्षण और उपकरण')}</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-800/40 to-emerald-900/40 border border-cyan-400/20 rounded-2xl p-5 text-center">
                  <div className="text-3xl mb-2">🏫</div>
                  <h5 className="text-white font-bold mb-1">{t('For School Leadership', 'स्कूल नेतृत्व के लिए')}</h5>
                  <p className="text-cyan-200 text-xs">{t('Position your school as a wellness pioneer', 'अपने स्कूल को वेलनेस अग्रणी के रूप में स्थापित करें')}</p>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-cyan-600/20 border-2 border-emerald-400/30 rounded-2xl p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex-1">
                    <h5 className="text-2xl font-bold text-white mb-2">{t('Ready to bring quantum wellness to your school?', 'अपने स्कूल में क्वांटम वेलनेस लाने के लिए तैयार हैं?')}</h5>
                    <p className="text-emerald-100 text-sm">
                      {t(<>Book a free 30-minute Zoom orientation for your leadership &amp; counseling team. We&apos;ll walk you through the science, results, and integration plan.</>,
                        'अपनी लीडरशिप और काउंसलिंग टीम के लिए 30 मिनट का निःशुल्क ज़ूम सत्र बुक करें। हम विज्ञान, परिणाम और एकीकरण योजना समझाएँगे।')}
                    </p>
                  </div>
                  <a
                    href="https://wa.me/918920443069?text=Hi%2C%20I%20represent%20a%20school%20and%20I%27d%20like%20to%20explore%20Quantum%20Healing%20Zoom%20sessions%20for%20our%20counseling%20room%20and%20students.%20Please%20share%20details."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 flex items-center gap-2"
                    data-testid="schools-book-zoom-btn"
                    style={{boxShadow: '0 0 25px rgba(16,185,129,0.35)'}}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {t('Book School Zoom', 'स्कूल ज़ूम बुक करें')}
                  </a>
                </div>
              </div>

              {/* Discovery Call — Book a Free 30-min slot */}
              <div className="mt-6 bg-gradient-to-r from-purple-900/40 via-indigo-900/40 to-purple-900/40 border-2 border-purple-400/30 rounded-2xl p-6 md:p-7" data-testid="schools-discovery-call">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500/40 to-indigo-500/40 border border-purple-400/50 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-purple-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-block bg-purple-500/20 px-3 py-1 rounded-full border border-purple-400/40 mb-2">
                      <span className="text-purple-200 font-bold text-xs tracking-widest">{t('BOOK A DISCOVERY CALL', 'डिस्कवरी कॉल बुक करें')}</span>
                    </div>
                    <h5 className="text-xl md:text-2xl font-bold text-white mb-1">{t('Free 30-min Zoom / Google Meet', 'निःशुल्क 30-मिनट ज़ूम / गूगल मीट')}</h5>
                    <p className="text-purple-200 text-sm">{t(<>Message us on WhatsApp and we&apos;ll confirm your slot within a few hours — no cost, no commitment. See how quantum wellness fits your school&apos;s counseling room.</>,
                      'हमें व्हाट्सएप पर संदेश भेजें, कुछ ही घंटों में स्लॉट कन्फर्म होगा — न कोई शुल्क, न कोई बाध्यता। देखिए क्वांटम वेलनेस आपके स्कूल के काउंसलिंग रूम में कैसे काम करती है।')}</p>
                  </div>
                  <a
                    href="https://wa.me/918920443069?text=Hello%20Divine%20Quanta%20Team%2C%0A%0AI%27d%20like%20to%20book%20a%20*Free%2030-min%20Discovery%20Call*%20for%20our%20*school*%20to%20explore%20Quantum%20Healing%20for%20our%20counseling%20room%20and%20students.%0A%0AKindly%20share%20the%20next%20available%20slots.%0A%0AThank%20you.%20%F0%9F%99%8F"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap bg-white text-purple-900 hover:bg-purple-50 px-6 py-3 rounded-full font-bold transition-all hover:scale-105 shadow-lg flex items-center gap-2"
                    data-testid="schools-discovery-cta"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {t('Request a Slot', 'स्लॉट का अनुरोध करें')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VIP & Premium Clients Section */}
        <div className="mt-32" data-testid="vip-clients-section">
          <div className="relative">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-900/30 via-yellow-900/40 to-orange-900/30 blur-3xl"></div>
            
            <div className="relative bg-gradient-to-br from-slate-900/80 via-amber-950/80 to-yellow-950/80 backdrop-blur-xl border-2 border-amber-400/30 rounded-3xl p-8 md:p-12 shadow-2xl">
              {/* Header */}
              <div className="text-center mb-12">
                <div className="inline-block bg-amber-500/20 px-6 py-2 rounded-full mb-6 border border-amber-400/40">
                  <span className="text-amber-300 font-bold text-sm">👑 EXCLUSIVE PROGRAM</span>
                </div>
                <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                  Own the Technology
                </h3>
                <p className="text-xl text-amber-100 max-w-3xl mx-auto">
                  For Celebrities, Bureaucrats & High-Profile Clients seeking to own quantum healing technology for personal use
                </p>
              </div>

              {/* Client Categories */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                {/* Celebrities */}
                <div className="bg-gradient-to-br from-amber-800/40 to-yellow-900/40 backdrop-blur-sm border border-amber-400/20 rounded-2xl p-6 hover:scale-105 transition-all text-center">
                  <div className="bg-amber-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">⭐</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">Celebrities</h4>
                  <p className="text-amber-200 text-sm">
                    Film stars, sports personalities, influencers looking for cutting-edge wellness technology
                  </p>
                </div>

                {/* Bureaucrats */}
                <div className="bg-gradient-to-br from-yellow-800/40 to-orange-900/40 backdrop-blur-sm border border-yellow-400/20 rounded-2xl p-6 hover:scale-105 transition-all text-center">
                  <div className="bg-yellow-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">🏛️</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">Bureaucrats & Officials</h4>
                  <p className="text-yellow-200 text-sm">
                    Government officials, diplomats, and senior executives seeking stress management solutions
                  </p>
                </div>

                {/* High Profile */}
                <div className="bg-gradient-to-br from-orange-800/40 to-red-900/40 backdrop-blur-sm border border-orange-400/20 rounded-2xl p-6 hover:scale-105 transition-all text-center">
                  <div className="bg-orange-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">💎</span>
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3">High-Profile Clients</h4>
                  <p className="text-orange-200 text-sm">
                    Business tycoons, industrialists, and VIPs who want exclusive access to quantum healing
                  </p>
                </div>
              </div>

              {/* Why Own the Device */}
              <div className="bg-gradient-to-r from-amber-900/30 to-yellow-900/30 border border-amber-400/20 rounded-2xl p-8 mb-8">
                <h4 className="text-2xl font-bold text-white text-center mb-6">Why Choose Quantum Science Therapy?</h4>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="bg-amber-500/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-7 h-7 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-amber-100 text-sm font-medium">24/7 Personal Access</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-yellow-500/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-7 h-7 text-yellow-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <p className="text-yellow-100 text-sm font-medium">Complete Privacy</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-orange-500/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-7 h-7 text-orange-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <p className="text-orange-100 text-sm font-medium">Family Wellness</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-500/20 p-3 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-7 h-7 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <p className="text-red-100 text-sm font-medium">Expert Training</p>
                  </div>
                </div>
              </div>

              {/* Process Steps */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-white text-center mb-6">How It Works</h4>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-amber-900/30 border border-amber-400/20 rounded-xl p-4 text-center">
                    <div className="bg-amber-500 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">1</div>
                    <p className="text-amber-100 text-sm font-medium">Submit Inquiry</p>
                  </div>
                  <div className="bg-yellow-900/30 border border-yellow-400/20 rounded-xl p-4 text-center">
                    <div className="bg-yellow-500 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">2</div>
                    <p className="text-yellow-100 text-sm font-medium">Zoom Consultation</p>
                  </div>
                  <div className="bg-orange-900/30 border border-orange-400/20 rounded-xl p-4 text-center">
                    <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">3</div>
                    <p className="text-orange-100 text-sm font-medium">Device Selection</p>
                  </div>
                  <div className="bg-red-900/30 border border-red-400/20 rounded-xl p-4 text-center">
                    <div className="bg-red-500 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">4</div>
                    <p className="text-red-100 text-sm font-medium">Training & Support</p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center">
                <p className="text-amber-200 mb-6 text-lg">
                  Interested in owning the quantum healing technology? Connect with us for a private consultation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="https://wa.me/918920443069?text=Hi,%20I'm%20a%20VIP/High-Profile%20client%20interested%20in%20Quantum%20Science%20Therapy.%20Please%20arrange%20a%20private%20consultation."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 inline-flex items-center justify-center space-x-2"
                    data-testid="vip-whatsapp-btn"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    <span>Private Consultation</span>
                  </a>
                  <a 
                    href="https://wa.me/918920443069?text=Hi,%20I%20would%20like%20to%20schedule%20a%20Zoom%20session%20to%20understand%20Quantum%20Science%20Therapy%20before%20booking."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 inline-flex items-center justify-center space-x-2"
                    data-testid="vip-zoom-btn"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Zoom Demo Session</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section - WhatsApp Only */}
        <div className="mt-32">
          <div className="text-center mb-12">
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('Book Your Session', 'अपना सत्र बुक करें')}
            </h3>
            <p className="text-lg text-purple-200 max-w-2xl mx-auto">
              {t('Connect with us on WhatsApp to book your quantum healing session', 'अपना क्वांटम हीलिंग सत्र बुक करने के लिए हमसे व्हाट्सएप पर जुड़ें')}
            </p>
          </div>

          <div className="flex justify-center mb-16">
            {/* WhatsApp Only */}
            <a 
              href="https://wa.me/918920443069?text=Hi,%20I%20want%20to%20book%20a%20quantum%20healing%20session%20with%20Divine%20Quanta"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 backdrop-blur-lg border-2 border-green-500 rounded-3xl p-6 md:p-12 text-center transition-all hover:scale-105 shadow-2xl max-w-md"
              data-testid="contact-whatsapp"
            >
              <div className="bg-white/30 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <h4 className="text-3xl font-bold text-white mb-4">{t('WhatsApp Booking', 'व्हाट्सएप बुकिंग')}</h4>
              <p className="text-green-100 text-lg">{t('Click to book instantly on WhatsApp', 'व्हाट्सएप पर तुरंत बुक करने के लिए क्लिक करें')}</p>
            </a>
          </div>
        </div>

        {/* Testimonials Section with YouTube Link */}
        <div className="mt-16" data-testid="testimonials-section">
          <div className="text-center mb-12">
            <div className="inline-block bg-yellow-500/20 px-6 py-2 rounded-full mb-6 border border-yellow-400/40">
              <span className="text-yellow-300 font-bold text-sm">⭐ WORLD TESTIMONIALS</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t('Real Healing Stories', 'सच्ची उपचार कहानियाँ')}
            </h3>
            <p className="text-purple-200 text-lg max-w-2xl mx-auto mb-8">
              {t('See what people around the world are saying about Quantum Science Therapy', 'देखिए दुनिया भर के लोग क्वांटम साइंस थेरेपी के बारे में क्या कह रहे हैं')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur-xl border-2 border-yellow-400/30 rounded-3xl p-8 md:p-12 max-w-4xl mx-auto">
            {/* YouTube Testimonials Link */}
            <div className="text-center mb-8">
              <a 
                href="https://www.youtube.com/results?search_query=quantum+science+therapy+healing+testimony"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-xl"
                data-testid="youtube-testimonials-link"
                style={{boxShadow: '0 0 30px rgba(220, 38, 38, 0.4)'}}
              >
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                <span>{t('Watch World Testimonials on YouTube', 'यूट्यूब पर विश्व के अनुभव देखें')}</span>
              </a>
            </div>

            {/* Featured Testimonials */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-purple-800/30 backdrop-blur-sm border border-purple-400/20 rounded-2xl p-6">
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-purple-100 italic mb-4">
                  "After just 3 sessions, my chronic pain reduced significantly. The distance healing really works - I felt the energy from my home in Mumbai!"
                </p>
                <p className="text-yellow-300 font-semibold">- Priya S., Mumbai</p>
              </div>

              <div className="bg-purple-800/30 backdrop-blur-sm border border-purple-400/20 rounded-2xl p-6">
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-purple-100 italic mb-4">
                  &ldquo;The Divine Quanta team&apos;s quantum healing helped me overcome anxiety I&apos;d struggled with for years. The aura analysis was eye-opening!&rdquo;
                </p>
                <p className="text-yellow-300 font-semibold">- Amit K., Delhi</p>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-purple-200 text-sm">
                🌍 Join thousands of people worldwide experiencing quantum transformation
              </p>
            </div>
          </div>
        </div>

        {/* Voice Recording Section */}
        <div className="mt-32" data-testid="voice-recording-section">
          <div className="text-center mb-12">
            <div className="inline-block bg-red-500/20 px-6 py-2 rounded-full mb-6 border border-red-400/40">
              <span className="text-red-300 font-bold text-sm">{t('DESCRIBE YOUR HEALTH CONCERN', 'अपनी स्वास्थ्य समस्या बताएं')}</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t(<>Record Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-300 to-pink-300">Voice Message</span></>,
                <>अपना <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-300 to-pink-300">वॉइस मैसेज रिकॉर्ड करें</span></>)}
            </h3>
            <p className="text-purple-200 text-lg max-w-3xl mx-auto">
              {t("Can't type it all? Simply record a 2-3 minute voice message describing your health concerns, and our therapist will personally listen and respond with a healing plan.",
                'सब कुछ टाइप नहीं कर सकते? बस 2-3 मिनट का वॉइस मैसेज रिकॉर्ड करें जिसमें अपनी स्वास्थ्य समस्याएँ बताएं — हमारी थेरेपिस्ट स्वयं सुनकर उपचार योजना के साथ उत्तर देंगी।')}
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur-xl border border-red-400/20 rounded-3xl p-8 md:p-12 max-w-3xl mx-auto" style={{boxShadow: '0 0 40px rgba(239,68,68,0.08)'}}>
            <VoiceRecorder />
          </div>
        </div>

        {/* Membership Section */}
        <div className="mt-32" data-testid="membership-section">
          <div className="text-center mb-12">
            <div className="inline-block bg-amber-500/20 px-6 py-2 rounded-full mb-6 border border-amber-400/40">
              <span className="text-amber-300 font-bold text-sm">{t('FOR EXISTING THERAPY CLIENTS', 'मौजूदा थेरेपी क्लाइंट्स के लिए')}</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t(<>Divine Quanta <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-300">Membership</span></>,
                <>डिवाइन क्वांटा <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-yellow-300">सदस्यता</span></>)}
            </h3>
            <p className="text-purple-200 text-lg max-w-3xl mx-auto mb-2">
              {t('Have you experienced quantum healing with us? Become a member and continue your transformation journey with exclusive benefits, priority access, and ongoing support.',
                'क्या आपने हमारे साथ क्वांटम हीलिंग का अनुभव किया है? सदस्य बनें और विशेष लाभ, प्राथमिकता और निरंतर सहयोग के साथ अपनी परिवर्तन यात्रा जारी रखें।')}
            </p>
            <p className="text-amber-300 font-semibold text-lg">
              {t('One-time membership fee: Rs 500 only', 'एक बार का सदस्यता शुल्क: केवल ₹500')}
            </p>
          </div>

          <MembershipSection />
        </div>

        {/* CTA Section */}
        <div className="mt-32 text-center">
          <div className="bg-gradient-to-r from-purple-900/60 via-indigo-900/60 to-cyan-900/60 backdrop-blur-xl border border-cyan-400/20 rounded-3xl p-8 md:p-16" style={{boxShadow: '0 0 80px rgba(34,211,238,0.08), 0 0 40px rgba(168,85,247,0.08)'}}>
            <div className="inline-block bg-cyan-500/20 px-6 py-2 rounded-full mb-6 border border-cyan-400/40">
              <span className="text-cyan-300 font-bold text-sm">INITIATE QUANTUM CONNECTION</span>
            </div>
            <h3 className="text-4xl lg:text-5xl font-bold text-white mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              {t(<>Ready to Experience<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-300">Quantum Transformation?</span></>,
                <>तैयार हैं अनुभव करने के लिए<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-300">क्वांटम परिवर्तन?</span></>)}
            </h3>
            <p className="text-lg text-purple-200 mb-8 max-w-2xl mx-auto">
              {t('Your particles are already entangled with the universal quantum field. Take the first step to align your bio-energetic frequency with optimal health and consciousness.',
                'आपके कण पहले से ही सार्वभौमिक क्वांटम क्षेत्र से जुड़े हुए हैं। श्रेष्ठ स्वास्थ्य और चेतना के साथ अपनी जैव-ऊर्जा आवृत्ति को संरेखित करने का पहला कदम उठाएँ।')}
            </p>
            <Button 
              data-testid="cta-get-started-btn"
              onClick={() => navigate('/pay')} 
              className="bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white px-12 py-6 rounded-full text-lg font-bold"
              style={{boxShadow: '0 0 30px rgba(34,211,238,0.3), 0 0 60px rgba(168,85,247,0.2)'}}
            >
              <Atom className="w-5 h-5 mr-2" />
              {t('Begin Quantum Session', 'क्वांटम सत्र शुरू करें')}
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-purple-500/30 mt-32">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Sparkles className="w-6 h-6 text-purple-300" />
              <span className="text-white font-bold" style={{fontFamily: 'Cormorant Garamond, serif'}}>Divine Quanta</span>
            </div>
            <p className="text-purple-300 text-sm">© 2025 Divine Quanta. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
